// src/common/MeshBackground.tsx
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var MOBILE_BREAKPOINT = 768;
var POLYGON_SIDES = 6;
var SHARED_ROTATION = Math.PI / 6;
var BASE_STROKE_ALPHA = [0.055, 0.075, 0.1];
var BASE_STROKE_WIDTH = [1, 1.2, 1.35];
var INTERACTION_RADIUS = 180;
var EDGE_TRIGGER_DISTANCE = 44;
var MAX_PARALLAX = 10;
var EASING_SMOOTHING = 0.12;
var MAX_PULSES = 24;
var CORNER_ROUNDING = 0.1;
var CORNER_BEZIER_STEPS = 5;
var CURSOR_TRAIL_RADIUS = 50;
var CURSOR_TRAIL_MIN_LENGTH = 0.08;
var CURSOR_TRAIL_SPEED_LENGTH = 0.2;
var CIRCLE_SPEED_THRESHOLD = 0.22;
var CIRCLE_TURN_RATE_THRESHOLD = 22e-4;
var RELAY_RESONANCE_THRESHOLD = 0.78;
var MAX_PULSE_RELAY_PER_EVENT = 1;
var MIN_RELAY_COOLDOWN_MS = 260;
var MAX_RELAY_COOLDOWN_MS = 380;
var DESKTOP_ROWS = 2;
var DESKTOP_COLS = 2;
var MOBILE_ROWS = 2;
var MOBILE_COLS = 1;
var ANCHOR_INSET_X = 0.08;
var DESKTOP_ANCHOR_INSET_Y = 0.12;
var MOBILE_ANCHOR_INSET_Y = 0.18;
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
function mediaMatches(query) {
  if (typeof window.matchMedia !== "function") return false;
  return window.matchMedia(query).matches;
}
function useMediaQuery(query) {
  return useSyncExternalStore(
    (notify) => {
      const mq = window.matchMedia(query);
      mq.addEventListener("change", notify);
      return () => mq.removeEventListener("change", notify);
    },
    () => window.matchMedia(query).matches,
    () => false
  );
}
function pointAtT(x1, y1, x2, y2, t) {
  return {
    x: x1 + (x2 - x1) * t,
    y: y1 + (y2 - y1) * t
  };
}
function distanceBetween(a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}
function normalizeAngleDelta(delta) {
  let next = delta;
  while (next > Math.PI) next -= Math.PI * 2;
  while (next < -Math.PI) next += Math.PI * 2;
  return next;
}
function buildSmoothPath(vertices) {
  const n = vertices.length;
  if (n < 3) return vertices;
  const path = [];
  for (let i = 0; i < n; i++) {
    const prev = vertices[(i - 1 + n) % n];
    const curr = vertices[i];
    const next = vertices[(i + 1) % n];
    const prevLen = distanceBetween(curr, prev);
    const nextLen = distanceBetween(curr, next);
    const r = Math.min(prevLen, nextLen) * Math.min(CORNER_ROUNDING, 0.4);
    const entryX = curr.x + (prev.x - curr.x) / prevLen * r;
    const entryY = curr.y + (prev.y - curr.y) / prevLen * r;
    const exitX = curr.x + (next.x - curr.x) / nextLen * r;
    const exitY = curr.y + (next.y - curr.y) / nextLen * r;
    for (let step = 0; step <= CORNER_BEZIER_STEPS; step++) {
      const t = step / CORNER_BEZIER_STEPS;
      const mt = 1 - t;
      path.push({
        x: mt * mt * entryX + 2 * mt * t * curr.x + t * t * exitX,
        y: mt * mt * entryY + 2 * mt * t * curr.y + t * t * exitY
      });
    }
  }
  path.push({ x: path[0].x, y: path[0].y });
  return path;
}
function distanceToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) {
    const distX2 = px - x1;
    const distY2 = py - y1;
    return { distance: Math.sqrt(distX2 * distX2 + distY2 * distY2), t: 0 };
  }
  const t = clamp(((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy), 0, 1);
  const projectedX = x1 + t * dx;
  const projectedY = y1 + t * dy;
  const distX = px - projectedX;
  const distY = py - projectedY;
  return { distance: Math.sqrt(distX * distX + distY * distY), t };
}
function getDevicePowerProfile() {
  const reducedMotion = mediaMatches("(prefers-reduced-motion: reduce)");
  const coarsePointer = mediaMatches("(pointer: coarse)");
  const lowCpu = typeof navigator !== "undefined" && navigator.hardwareConcurrency <= 4;
  const lowMemory = typeof navigator !== "undefined" && "deviceMemory" in navigator && (navigator.deviceMemory ?? 8) <= 4;
  return {
    reducedMotion,
    lowPower: reducedMotion || coarsePointer || lowCpu || lowMemory
  };
}
function buildStaggeredAnchors(rows, cols, insetX, insetY) {
  const anchors = [];
  const cellWidth = (1 - insetX * 2) / cols;
  const rowStep = rows > 1 ? (1 - insetY * 2) / (rows - 1) : 0;
  for (let row = 0; row < rows; row++) {
    const shift = row % 2 === 0 ? 0 : 0.5;
    for (let col = 0; col < cols; col++) {
      const x = insetX + (col + 0.5 + shift) * cellWidth;
      if (x < insetX || x > 1 - insetX) continue;
      const y = insetY + row * rowStep;
      anchors.push([x, y]);
    }
  }
  return anchors;
}
function createPolygons(width, height, lowPower) {
  const isMobile = width < MOBILE_BREAKPOINT;
  const aspect = height / width;
  const baseCols = isMobile ? MOBILE_COLS : DESKTOP_COLS;
  const baseRows = isMobile ? MOBILE_ROWS : DESKTOP_ROWS;
  const insetY = isMobile ? MOBILE_ANCHOR_INSET_Y : DESKTOP_ANCHOR_INSET_Y;
  const scale = Math.max(1, Math.sqrt(aspect));
  const cols = Math.max(baseCols, Math.round(baseCols * scale));
  const rows = Math.max(baseRows, Math.round(baseRows * scale));
  const anchors = buildStaggeredAnchors(rows, cols, ANCHOR_INSET_X, insetY);
  const cellWidth = (1 - ANCHOR_INSET_X * 2) / cols * width;
  const cellHeight = rows > 1 ? (1 - insetY * 2) / (rows - 1) * height : height;
  const cellSize = Math.min(cellWidth, cellHeight);
  const radiusFactor = isMobile ? 0.48 : 0.43;
  const baseRadius = cellSize * radiusFactor;
  const vertices = [];
  for (let i = 0; i < POLYGON_SIDES; i++) {
    const angle = SHARED_ROTATION + Math.PI * 2 / POLYGON_SIDES * i;
    vertices.push({ x: Math.cos(angle) * baseRadius, y: Math.sin(angle) * baseRadius });
  }
  const basePath = buildSmoothPath(vertices);
  return anchors.map(([nx, ny], index) => {
    const depth = index % 3;
    return {
      id: index,
      x: nx * width,
      y: ny * height,
      radius: baseRadius,
      depth,
      rotation: SHARED_ROTATION,
      phase: index * 1.17,
      driftX: lowPower ? 0.15 : 0.22 + depth * 0.05,
      driftY: lowPower ? 0.12 : 0.2 + depth * 0.05,
      basePath
    };
  });
}
function strokeGlowLine(ctx, x1, y1, x2, y2, alpha, coreWidth, r, g, b) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.08})`;
  ctx.lineWidth = coreWidth + 8;
  ctx.stroke();
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.2})`;
  ctx.lineWidth = coreWidth + 4;
  ctx.stroke();
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.5})`;
  ctx.lineWidth = coreWidth + 1.5;
  ctx.stroke();
  ctx.strokeStyle = `rgba(224, 247, 255, ${clamp(alpha * 0.8, 0.1, 0.9)})`;
  ctx.lineWidth = coreWidth;
  ctx.stroke();
}
function pushUniqueNeighbor(map, key, value) {
  const existing = map.get(key);
  if (!existing) {
    map.set(key, [value]);
    return;
  }
  if (!existing.includes(value)) {
    existing.push(value);
  }
}
function chooseUniqueRandomItems(items, count) {
  if (count <= 0 || items.length === 0) return [];
  if (count >= items.length) return items.slice();
  const result = [];
  const selected = /* @__PURE__ */ new Set();
  while (result.length < count) {
    const index = Math.floor(Math.random() * items.length);
    if (selected.has(index)) continue;
    selected.add(index);
    result.push(items[index]);
  }
  return result;
}
function MeshBackground({ mode = "viewport", className = "" }) {
  const isReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const isMobile = useMediaQuery("(max-width: 767px)");
  const canvasRef = useRef(null);
  const rafIdRef = useRef(0);
  const polygonsRef = useRef([]);
  const neighborsRef = useRef(/* @__PURE__ */ new Map());
  const pathLinksRef = useRef(/* @__PURE__ */ new Map());
  const polygonSegmentIdsRef = useRef(/* @__PURE__ */ new Map());
  const runtimeSegmentsRef = useRef([]);
  const runtimePolygonsRef = useRef([]);
  const pulsesRef = useRef([]);
  const pulseIdRef = useRef(0);
  const lowPowerRef = useRef(false);
  const reducedMotionRef = useRef(false);
  const lastPulseTimestampRef = useRef(0);
  const resonanceRef = useRef(0);
  const resonanceTargetRef = useRef(0);
  const lastRelayTimestampRef = useRef(0);
  const initialPulsesSpawnedRef = useRef(false);
  const initialGlowsRef = useRef([]);
  const dprRef = useRef(1);
  const lastGeometryRebuildRef = useRef(-Infinity);
  const mouseRef = useRef({
    x: -1e3,
    y: -1e3,
    inside: false,
    lastX: -1e3,
    lastY: -1e3,
    lastTime: 0,
    targetSpeed: 0,
    speed: 0,
    targetParallaxX: 0,
    targetParallaxY: 0,
    parallaxX: 0,
    parallaxY: 0,
    moveX: 0,
    moveY: 0
  });
  const rebuildRuntimeGeometry = useCallback((time, width, height) => {
    const mouse = mouseRef.current;
    runtimeSegmentsRef.current = [];
    runtimePolygonsRef.current = [];
    neighborsRef.current = /* @__PURE__ */ new Map();
    pathLinksRef.current = /* @__PURE__ */ new Map();
    polygonSegmentIdsRef.current = /* @__PURE__ */ new Map();
    let segmentId = 0;
    const transformedPolygons = polygonsRef.current.map((polygon) => {
      const depthRatio = polygon.depth === 2 ? 1 : polygon.depth === 1 ? 0.7 : 0.45;
      const driftX = Math.sin(time * 27e-5 + polygon.phase) * polygon.driftX;
      const driftY = Math.cos(time * 23e-5 + polygon.phase * 1.15) * polygon.driftY;
      return {
        ...polygon,
        x: clamp(polygon.x + driftX + mouse.parallaxX * depthRatio, -200, width + 200),
        y: clamp(polygon.y + driftY + mouse.parallaxY * depthRatio, -200, height + 200),
        radius: polygon.radius
      };
    });
    for (const polygon of transformedPolygons) {
      const roundedPath = polygon.basePath.map((p) => ({ x: p.x + polygon.x, y: p.y + polygon.y }));
      runtimePolygonsRef.current.push({
        id: polygon.id,
        depth: polygon.depth,
        roundedPath
      });
      const polygonSegmentIds = [];
      for (let i = 0; i < roundedPath.length - 1; i++) {
        const start = roundedPath[i];
        const end = roundedPath[i + 1];
        const length = distanceBetween(start, end);
        if (length <= 1e-3) continue;
        const id = segmentId++;
        runtimeSegmentsRef.current.push({
          id,
          polygonId: polygon.id,
          x1: start.x,
          y1: start.y,
          x2: end.x,
          y2: end.y,
          length,
          depth: polygon.depth
        });
        neighborsRef.current.set(id, []);
        polygonSegmentIds.push(id);
      }
      if (polygonSegmentIds.length > 1) {
        polygonSegmentIdsRef.current.set(polygon.id, polygonSegmentIds);
        const count = polygonSegmentIds.length;
        for (let i = 0; i < count; i++) {
          const current = polygonSegmentIds[i];
          const backward = polygonSegmentIds[(i - 1 + count) % count];
          const forward = polygonSegmentIds[(i + 1) % count];
          pathLinksRef.current.set(current, { forward, backward });
          pushUniqueNeighbor(neighborsRef.current, current, backward);
          pushUniqueNeighbor(neighborsRef.current, current, forward);
        }
      }
    }
  }, []);
  const spawnPulse = useCallback(
    (segmentId, t, speed, intensityScale = 1, direction = Math.random() > 0.5 ? 1 : -1) => {
      const segment = runtimeSegmentsRef.current[segmentId];
      if (segment) {
        const polygonId = segment.polygonId;
        const alreadyActive = pulsesRef.current.some((p) => {
          const seg = runtimeSegmentsRef.current[p.segmentId];
          return seg && seg.polygonId === polygonId;
        });
        if (alreadyActive) return;
      }
      const normalizedSpeed = clamp(speed, 0, 1);
      const intensity = clamp((0.28 + normalizedSpeed * 0.55) * intensityScale, 0.18, 0.82);
      const pulse = {
        id: pulseIdRef.current++,
        segmentId,
        t: clamp(t, 0, 1),
        direction,
        velocityPerMs: 45e-5 + normalizedSpeed * 105e-5,
        length: 0.09 + normalizedSpeed * 0.08,
        intensity,
        lifeMs: 520 + normalizedSpeed * 280,
        ageMs: 0
      };
      pulsesRef.current.push(pulse);
      if (pulsesRef.current.length > MAX_PULSES) {
        pulsesRef.current.splice(0, pulsesRef.current.length - MAX_PULSES);
      }
    },
    []
  );
  const relayPulseAcrossPolygons = useCallback(
    (originSegmentId, speed, resonance) => {
      if (resonance < RELAY_RESONANCE_THRESHOLD || lowPowerRef.current || reducedMotionRef.current)
        return;
      const now = performance.now();
      const relayCooldown = clamp(
        MAX_RELAY_COOLDOWN_MS - resonance * 55,
        MIN_RELAY_COOLDOWN_MS,
        MAX_RELAY_COOLDOWN_MS
      );
      if (now - lastRelayTimestampRef.current < relayCooldown) return;
      const originSegment = runtimeSegmentsRef.current[originSegmentId];
      if (!originSegment) return;
      const remotePolygonIds = Array.from(polygonSegmentIdsRef.current.keys()).filter(
        (polygonId) => polygonId !== originSegment.polygonId
      );
      if (remotePolygonIds.length === 0) return;
      const relayCount = Math.min(
        Math.floor(resonance * MAX_PULSE_RELAY_PER_EVENT),
        remotePolygonIds.length
      );
      if (relayCount <= 0) return;
      const selectedPolygonIds = chooseUniqueRandomItems(remotePolygonIds, relayCount);
      const relaySpeed = clamp(speed * 0.28 + resonance * 0.16, 0, 0.36);
      const intensityScale = clamp(0.24 + resonance * 0.12, 0.2, 0.34);
      for (const polygonId of selectedPolygonIds) {
        const candidateSegments = polygonSegmentIdsRef.current.get(polygonId) ?? [];
        if (candidateSegments.length === 0) continue;
        const segmentId = candidateSegments[Math.floor(Math.random() * candidateSegments.length)];
        const direction = Math.random() > 0.5 ? 1 : -1;
        spawnPulse(segmentId, Math.random(), relaySpeed, intensityScale, direction);
      }
      lastRelayTimestampRef.current = now;
    },
    [spawnPulse]
  );
  const drawScene = useCallback(
    (ctx, width, height, time, deltaMs) => {
      ctx.clearRect(0, 0, width, height);
      ctx.lineJoin = "round";
      ctx.lineCap = "butt";
      const mouse = mouseRef.current;
      mouse.speed += (mouse.targetSpeed - mouse.speed) * EASING_SMOOTHING;
      mouse.targetSpeed *= 0.93;
      mouse.parallaxX += (mouse.targetParallaxX - mouse.parallaxX) * EASING_SMOOTHING;
      mouse.parallaxY += (mouse.targetParallaxY - mouse.parallaxY) * EASING_SMOOTHING;
      resonanceTargetRef.current *= 0.992;
      const resonanceTarget = clamp(resonanceTargetRef.current + mouse.speed * 0.62, 0, 1);
      resonanceRef.current += (resonanceTarget - resonanceRef.current) * 0.22;
      const resonance = clamp(resonanceRef.current, 0, 1);
      if (time - lastGeometryRebuildRef.current >= 48) {
        rebuildRuntimeGeometry(time, width, height);
        lastGeometryRebuildRef.current = time;
      }
      const breathingLight = 1;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      for (const polygon of runtimePolygonsRef.current) {
        const baseAlpha = BASE_STROKE_ALPHA[polygon.depth] * breathingLight;
        const baseWidth = BASE_STROKE_WIDTH[polygon.depth];
        const points = polygon.roundedPath;
        if (points.length < 2) continue;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(72, 124, 210, ${baseAlpha})`;
        ctx.lineWidth = baseWidth;
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.closePath();
        ctx.stroke();
      }
      ctx.lineCap = "butt";
      ctx.lineJoin = "round";
      let nearestSegmentId = -1;
      let nearestDistance = Number.POSITIVE_INFINITY;
      let nearestT = 0;
      const minDistPerPolygon = /* @__PURE__ */ new Map();
      for (const segment of runtimeSegmentsRef.current) {
        if (!mouse.inside || lowPowerRef.current || reducedMotionRef.current) break;
        const nearest = distanceToSegment(
          mouse.x,
          mouse.y,
          segment.x1,
          segment.y1,
          segment.x2,
          segment.y2
        );
        if (nearest.distance < nearestDistance) {
          nearestDistance = nearest.distance;
          nearestSegmentId = segment.id;
          nearestT = nearest.t;
        }
        const prev = minDistPerPolygon.get(segment.polygonId) ?? Number.POSITIVE_INFINITY;
        if (nearest.distance < prev) minDistPerPolygon.set(segment.polygonId, nearest.distance);
      }
      if (mouse.inside && !lowPowerRef.current && !reducedMotionRef.current) {
        ctx.globalCompositeOperation = "lighter";
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        for (const polygon of runtimePolygonsRef.current) {
          const minDist = minDistPerPolygon.get(polygon.id) ?? Number.POSITIVE_INFINITY;
          const proximity = clamp(1 - minDist / INTERACTION_RADIUS, 0, 1);
          if (proximity <= 0.01) continue;
          const points = polygon.roundedPath;
          if (points.length < 2) continue;
          const baseWidth = BASE_STROKE_WIDTH[polygon.depth];
          const glowAlpha = clamp(proximity * (0.52 + resonance * 0.22), 0, 0.72);
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
          ctx.closePath();
          const gradRadius = INTERACTION_RADIUS * (0.45 + proximity * 0.55);
          const grad = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, gradRadius);
          grad.addColorStop(0, `rgba(44, 192, 255, ${glowAlpha})`);
          grad.addColorStop(0.55, `rgba(44, 160, 240, ${glowAlpha * 0.4})`);
          grad.addColorStop(1, "rgba(34, 100, 200, 0)");
          ctx.strokeStyle = grad;
          ctx.lineWidth = baseWidth + proximity * 6 + resonance * 2;
          ctx.globalAlpha = 0.3;
          ctx.stroke();
          ctx.lineWidth = baseWidth + proximity * 3.5 + resonance * 1;
          ctx.globalAlpha = 0.6;
          ctx.stroke();
          ctx.globalAlpha = 1;
          ctx.lineWidth = baseWidth + proximity * 2.2 + resonance * 0.7;
          ctx.stroke();
          ctx.strokeStyle = `rgba(210, 244, 255, ${clamp(proximity * 0.42 + resonance * 0.1, 0, 0.65)})`;
          ctx.lineWidth = baseWidth * 0.55 + proximity * 0.6 + resonance * 0.2;
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
      }
      if (nearestSegmentId >= 0 && nearestDistance <= CURSOR_TRAIL_RADIUS && mouse.inside && mouse.speed > 0.04 && !lowPowerRef.current && !reducedMotionRef.current) {
        const segment = runtimeSegmentsRef.current[nearestSegmentId];
        if (segment) {
          const segmentDx = segment.x2 - segment.x1;
          const segmentDy = segment.y2 - segment.y1;
          const dot = segmentDx * mouse.moveX + segmentDy * mouse.moveY;
          const followDirection = dot >= 0 ? 1 : -1;
          const trailLength = CURSOR_TRAIL_MIN_LENGTH + mouse.speed * CURSOR_TRAIL_SPEED_LENGTH;
          const headT = nearestT;
          const tailT = clamp(headT - followDirection * trailLength, 0, 1);
          const head = pointAtT(segment.x1, segment.y1, segment.x2, segment.y2, headT);
          const tail = pointAtT(segment.x1, segment.y1, segment.x2, segment.y2, tailT);
          const followStrength = clamp(1 - nearestDistance / CURSOR_TRAIL_RADIUS, 0, 1);
          const alpha = clamp(0.12 + followStrength * 0.38 + resonance * 0.1, 0.14, 0.56);
          ctx.globalCompositeOperation = "lighter";
          ctx.lineCap = "round";
          strokeGlowLine(ctx, tail.x, tail.y, head.x, head.y, alpha, 0.75 + followStrength * 0.6 + resonance * 0.15, 56, 189, 248);
          ctx.lineCap = "butt";
          ctx.globalCompositeOperation = "source-over";
        }
      }
      if (nearestSegmentId >= 0 && nearestDistance <= EDGE_TRIGGER_DISTANCE && mouse.inside && mouse.speed > 0.06 && !lowPowerRef.current && !reducedMotionRef.current) {
        const now = performance.now();
        const cooldown = clamp(94 - mouse.speed * 32 - resonance * 18, 42, 94);
        if (now - lastPulseTimestampRef.current >= cooldown) {
          const segment = runtimeSegmentsRef.current[nearestSegmentId];
          const segmentDx = segment ? segment.x2 - segment.x1 : 0;
          const segmentDy = segment ? segment.y2 - segment.y1 : 0;
          const dot = segmentDx * mouse.moveX + segmentDy * mouse.moveY;
          const direction = dot >= 0 ? 1 : -1;
          const intensityScale = 0.72 + resonance * 0.2;
          spawnPulse(nearestSegmentId, nearestT, mouse.speed, intensityScale, direction);
          relayPulseAcrossPolygons(nearestSegmentId, mouse.speed, resonance);
          lastPulseTimestampRef.current = now;
        }
      }
      if (!initialPulsesSpawnedRef.current && !reducedMotionRef.current) {
        initialPulsesSpawnedRef.current = true;
        const allPolygons = [...runtimePolygonsRef.current].sort(() => Math.random() - 0.5);
        const glowCount = Math.max(1, Math.ceil(allPolygons.length * 0.5));
        allPolygons.slice(0, glowCount).forEach((polygon, i) => {
          initialGlowsRef.current.push({
            polygonId: polygon.id,
            // Stagger via progress offset so glows are at different positions
            // from the start — no pre-aging (avoids jump on loop start).
            progress: i / glowCount * 0.8 + Math.random() * 0.15,
            direction: Math.random() > 0.5 ? 1 : -1,
            speed: 6e-5 + Math.random() * 3e-5,
            // Short trail (< 1/6 perimeter) → stays within one edge, no corner wrapping
            trailLength: 0.13 + Math.random() * 0.04,
            intensity: 0.48 + Math.random() * 0.16,
            lifeMs: 2200 + Math.random() * 800,
            ageMs: 0
          });
        });
      }
      const nextPulses = [];
      for (const pulse of pulsesRef.current) {
        const nextAge = pulse.ageMs + deltaMs;
        const normalizedLife = clamp(nextAge / pulse.lifeMs, 0, 1);
        const fade = Math.exp(-normalizedLife * 0.92);
        let nextT = pulse.t + pulse.direction * pulse.velocityPerMs * deltaMs;
        let nextSegmentId = pulse.segmentId;
        let crossedCorners = 0;
        while ((nextT < 0 || nextT > 1) && crossedCorners < 3) {
          const link = pathLinksRef.current.get(nextSegmentId);
          if (!link) break;
          const throughPathId = pulse.direction === 1 ? link.forward : link.backward;
          if (nextT > 1) {
            nextT -= 1;
          } else {
            nextT += 1;
          }
          crossedCorners += 1;
          const branchingCandidates = (neighborsRef.current.get(nextSegmentId) ?? []).filter(
            (candidate) => candidate !== link.forward && candidate !== link.backward
          );
          nextSegmentId = throughPathId;
          const sourceSegment = runtimeSegmentsRef.current[nextSegmentId];
          const crossPolygonCandidates = sourceSegment ? branchingCandidates.filter((candidate) => {
            const candidateSegment = runtimeSegmentsRef.current[candidate];
            return candidateSegment && candidateSegment.polygonId !== sourceSegment.polygonId;
          }) : [];
          const preferredCandidates = crossPolygonCandidates.length > 0 && Math.random() < 0.28 + resonance * 0.16 ? crossPolygonCandidates : branchingCandidates;
          const propagationChance = clamp(
            0.18 + pulse.intensity * 0.2 + resonance * 0.25,
            0.08,
            0.62
          );
          if (preferredCandidates.length > 0 && Math.random() < propagationChance) {
            const candidate = preferredCandidates[Math.floor(Math.random() * preferredCandidates.length)];
            const candidateSegment = runtimeSegmentsRef.current[candidate];
            const sourcePolygonId = sourceSegment?.polygonId;
            const isCrossPolygon = candidateSegment && sourcePolygonId !== void 0 ? candidateSegment.polygonId !== sourcePolygonId : false;
            const propagatedSpeed = clamp(pulse.intensity * 0.68 + resonance * 0.22, 0.18, 0.72);
            const propagatedIntensityScale = isCrossPolygon ? 0.34 + resonance * 0.16 : 0.48 + resonance * 0.2;
            const propagatedDirection = Math.random() < 0.5 ? pulse.direction : pulse.direction === 1 ? -1 : 1;
            spawnPulse(
              candidate,
              Math.random(),
              propagatedSpeed,
              clamp(propagatedIntensityScale, 0.28, 0.68),
              propagatedDirection
            );
          }
        }
        const segment = runtimeSegmentsRef.current[nextSegmentId];
        if (!segment) continue;
        const headT = clamp(nextT, 0, 1);
        const tailT = clamp(headT - pulse.direction * pulse.length, 0, 1);
        const head = pointAtT(segment.x1, segment.y1, segment.x2, segment.y2, headT);
        const tail = pointAtT(segment.x1, segment.y1, segment.x2, segment.y2, tailT);
        const cornerDecay = 0.985 ** crossedCorners;
        const nextIntensity = pulse.intensity * cornerDecay;
        const alpha = clamp(nextIntensity * fade * 1.14, 0.08, 1);
        ctx.globalCompositeOperation = "lighter";
        strokeGlowLine(ctx, tail.x, tail.y, head.x, head.y, alpha, 0.95 + alpha * 1.15, 56, 189, 248);
        ctx.globalCompositeOperation = "source-over";
        if (nextAge < pulse.lifeMs) {
          nextPulses.push({
            ...pulse,
            segmentId: nextSegmentId,
            t: headT,
            intensity: nextIntensity,
            ageMs: nextAge
          });
        }
      }
      pulsesRef.current = nextPulses;
      const nextGlows = [];
      for (const glow of initialGlowsRef.current) {
        const nextAge = glow.ageMs + deltaMs;
        if (nextAge >= glow.lifeMs) continue;
        const polygon = runtimePolygonsRef.current.find((p) => p.id === glow.polygonId);
        if (!polygon || polygon.roundedPath.length < 3) continue;
        const path = polygon.roundedPath;
        const n = path.length;
        const nextProgress = glow.progress + glow.direction * glow.speed * deltaMs;
        const life = nextAge / glow.lifeMs;
        const fadeIn = clamp(life / 0.15, 0, 1);
        const fadeOut = clamp((1 - life) / 0.3, 0, 1);
        const fade = fadeIn * fadeOut;
        const alpha = glow.intensity * fade;
        if (alpha < 0.01) {
          nextGlows.push({ ...glow, progress: nextProgress, ageMs: nextAge });
          continue;
        }
        const headIdx = (Math.round(nextProgress * n) % n + n) % n;
        const trailPoints = Math.max(2, Math.round(glow.trailLength * n));
        const points = [];
        for (let i = trailPoints; i >= 0; i--) {
          const idx = glow.direction === 1 ? ((headIdx - i) % n + n) % n : ((headIdx + i) % n + n) % n;
          points.push(path[idx]);
        }
        if (points.length >= 2) {
          ctx.globalCompositeOperation = "lighter";
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
          ctx.strokeStyle = `rgba(56, 189, 248, ${alpha * 0.06})`;
          ctx.lineWidth = 6 + alpha * 4;
          ctx.stroke();
          ctx.strokeStyle = `rgba(125, 211, 252, ${alpha * 0.18})`;
          ctx.lineWidth = 3 + alpha * 2;
          ctx.stroke();
          ctx.strokeStyle = `rgba(200, 240, 255, ${alpha * 0.55})`;
          ctx.lineWidth = 1.2 + alpha * 1.2;
          ctx.stroke();
          ctx.lineCap = "butt";
          ctx.globalCompositeOperation = "source-over";
        }
        nextGlows.push({ ...glow, progress: nextProgress, ageMs: nextAge });
      }
      initialGlowsRef.current = nextGlows;
    },
    [rebuildRuntimeGeometry, relayPulseAcrossPolygons, spawnPulse]
  );
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const power = getDevicePowerProfile();
    reducedMotionRef.current = power.reducedMotion;
    lowPowerRef.current = power.lowPower;
    const resetCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = 1;
      dprRef.current = dpr;
      const width = Math.max(1, Math.round(rect.width || window.innerWidth));
      const height = Math.max(1, Math.round(rect.height || window.innerHeight));
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
      const polygons = createPolygons(width, height, lowPowerRef.current);
      polygonsRef.current = polygons;
      neighborsRef.current = /* @__PURE__ */ new Map();
      pathLinksRef.current = /* @__PURE__ */ new Map();
      runtimeSegmentsRef.current = [];
      pulsesRef.current = [];
      initialGlowsRef.current = [];
      resonanceRef.current = 0;
      resonanceTargetRef.current = 0;
      lastRelayTimestampRef.current = 0;
      initialPulsesSpawnedRef.current = false;
    };
    const handleResize = () => {
      resetCanvas();
      drawScene(
        ctx,
        canvas.width / dprRef.current,
        canvas.height / dprRef.current,
        performance.now(),
        16
      );
    };
    const handleMouseMove = (event) => {
      const now = performance.now();
      const mouse = mouseRef.current;
      const rect = canvas.getBoundingClientRect();
      const localX = event.clientX - rect.left;
      const localY = event.clientY - rect.top;
      const inside = localX >= 0 && localX <= rect.width && localY >= 0 && localY <= rect.height;
      if (!inside) {
        mouse.inside = false;
        mouse.targetSpeed = 0;
        mouse.targetParallaxX = 0;
        mouse.targetParallaxY = 0;
        return;
      }
      const dx = localX - mouse.lastX;
      const dy = localY - mouse.lastY;
      const dt = Math.max(8, now - mouse.lastTime);
      const velocity = Math.sqrt(dx * dx + dy * dy) / dt;
      const previousMoveX = mouse.moveX;
      const previousMoveY = mouse.moveY;
      const previousMoveMagnitude = Math.hypot(mouse.moveX, mouse.moveY);
      const currentMoveMagnitude = Math.sqrt(dx * dx + dy * dy);
      mouse.x = localX;
      mouse.y = localY;
      mouse.inside = true;
      mouse.lastX = localX;
      mouse.lastY = localY;
      mouse.lastTime = now;
      mouse.moveX = dx;
      mouse.moveY = dy;
      mouse.targetSpeed = Math.max(mouse.targetSpeed, clamp(velocity / 1.15, 0, 1));
      if (previousMoveMagnitude > 1 && currentMoveMagnitude > 1) {
        const previousAngle = Math.atan2(previousMoveY, previousMoveX);
        const currentAngle = Math.atan2(dy, dx);
        const turnRate = Math.abs(normalizeAngleDelta(currentAngle - previousAngle)) / dt;
        const speedSignal = clamp((velocity - CIRCLE_SPEED_THRESHOLD) / 0.8, 0, 1);
        const turnSignal = clamp((turnRate - CIRCLE_TURN_RATE_THRESHOLD) / 0.012, 0, 1);
        const orbitSignal = clamp(speedSignal * (0.45 + turnSignal * 0.9), 0, 1);
        resonanceTargetRef.current = Math.max(resonanceTargetRef.current * 0.94, orbitSignal);
      }
      if (!lowPowerRef.current) {
        const normX = localX / Math.max(1, rect.width) - 0.5;
        const normY = localY / Math.max(1, rect.height) - 0.5;
        mouse.targetParallaxX = normX * MAX_PARALLAX;
        mouse.targetParallaxY = normY * MAX_PARALLAX;
      }
    };
    const handleMouseLeave = () => {
      const mouse = mouseRef.current;
      mouse.inside = false;
      mouse.x = -1e3;
      mouse.y = -1e3;
      mouse.targetSpeed = 0;
      mouse.targetParallaxX = 0;
      mouse.targetParallaxY = 0;
      mouse.moveX = 0;
      mouse.moveY = 0;
      resonanceTargetRef.current = 0;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseout", handleMouseLeave);
    window.addEventListener("resize", handleResize);
    resetCanvas();
    drawScene(
      ctx,
      canvas.width / dprRef.current,
      canvas.height / dprRef.current,
      performance.now(),
      16
    );
    if (reducedMotionRef.current) {
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseout", handleMouseLeave);
        window.removeEventListener("resize", handleResize);
      };
    }
    let startTimerId = null;
    let animateFn = null;
    const startAnimationLoop = () => {
      let previousTime = performance.now();
      const animate = (time) => {
        if (document.hidden) return;
        const deltaMs = clamp(time - previousTime, 8, 34);
        previousTime = time;
        drawScene(
          ctx,
          canvas.width / dprRef.current,
          canvas.height / dprRef.current,
          time,
          deltaMs
        );
        rafIdRef.current = window.requestAnimationFrame(animate);
      };
      animateFn = animate;
      rafIdRef.current = window.requestAnimationFrame(animate);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        window.cancelAnimationFrame(rafIdRef.current);
      } else if (animateFn) {
        rafIdRef.current = window.requestAnimationFrame(animateFn);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    startTimerId = setTimeout(() => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(startAnimationLoop, { timeout: 200 });
      } else {
        startAnimationLoop();
      }
    }, 100);
    return () => {
      if (startTimerId) clearTimeout(startTimerId);
      window.cancelAnimationFrame(rafIdRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseout", handleMouseLeave);
      window.removeEventListener("resize", handleResize);
    };
  }, [drawScene]);
  if (isReducedMotion) return null;
  const wrapperClassName = (() => {
    const base = mode === "container" ? "absolute inset-0 z-0" : "fixed inset-0 z-0";
    return `${base} ${className}`.trim();
  })();
  const layerClassName = mode === "container" ? "absolute inset-0" : "fixed inset-0";
  return /* @__PURE__ */ jsxs("div", { className: wrapperClassName, style: { backgroundColor: "#03070f" }, children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        "data-testid": "ambient-gradients",
        "aria-hidden": "true",
        className: layerClassName,
        style: {
          backgroundImage: "radial-gradient(ellipse at 14% 16%, rgba(20, 48, 114, 0.17) 0%, transparent 60%), radial-gradient(ellipse at 82% 82%, rgba(14, 38, 92, 0.16) 0%, transparent 64%), linear-gradient(180deg, rgba(2,7,16,0.92) 0%, rgba(2,7,18,0.995) 100%)"
        }
      }
    ),
    !isMobile && /* @__PURE__ */ jsx("canvas", { ref: canvasRef, className: `${layerClassName} h-full w-full` }),
    !isMobile && /* @__PURE__ */ jsx(
      "div",
      {
        "data-testid": "grain-overlay",
        "aria-hidden": "true",
        className: `${layerClassName} z-[1] pointer-events-none`,
        style: { opacity: 6e-3 },
        children: /* @__PURE__ */ jsxs("svg", { width: "100%", height: "100%", children: [
          /* @__PURE__ */ jsx("filter", { id: "grain-filter", children: /* @__PURE__ */ jsx(
            "feTurbulence",
            {
              type: "fractalNoise",
              baseFrequency: "0.65",
              numOctaves: "3",
              stitchTiles: "stitch"
            }
          ) }),
          /* @__PURE__ */ jsx("rect", { width: "100%", height: "100%", filter: "url(#grain-filter)" })
        ] })
      }
    )
  ] });
}

// src/common/DarkMeshSection.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function DarkMeshSection({
  children,
  id,
  className = "",
  innerPadding = "px-6 py-10 sm:px-10 sm:py-12 lg:px-12 lg:py-14"
}) {
  return /* @__PURE__ */ jsx2("section", { id, className: `relative py-20 md:py-28 ${className}`, children: /* @__PURE__ */ jsx2("div", { className: "mx-auto w-full max-w-[1540px] px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs2(
    "div",
    {
      "data-nav-theme": "dark",
      className: "relative isolate overflow-hidden rounded-[30px] border border-[#dce6f6]/18 bg-[#0a1628] shadow-[0_26px_90px_rgba(7,18,40,0.45)]",
      children: [
        /* @__PURE__ */ jsx2(MeshBackground, { mode: "container" }),
        /* @__PURE__ */ jsx2(
          "div",
          {
            "aria-hidden": "true",
            className: "pointer-events-none absolute inset-0 z-[1]",
            style: {
              background: "radial-gradient(1200px 680px at 18% 4%, rgba(60,110,210,0.14), transparent 62%), radial-gradient(900px 520px at 82% 22%, rgba(45,102,198,0.14), transparent 66%), linear-gradient(180deg, rgba(6,11,22,0.18) 0%, rgba(7,12,24,0.32) 50%, rgba(7,13,26,0.48) 100%)"
            }
          }
        ),
        /* @__PURE__ */ jsx2("div", { className: `relative z-10 ${innerPadding}`, children })
      ]
    }
  ) }) });
}

// src/common/headCollectorContext.ts
import { createContext } from "react";
var HeadCollectorContext = createContext(null);

// src/common/headTags.ts
var metaKey = (m) => m.name !== void 0 ? `name=${m.name}` : `property=${m.property}`;
var linkKey = (l) => `${l.rel}|${l.hreflang ?? ""}`;
function createHeadCollector() {
  let lang;
  let title;
  const meta = /* @__PURE__ */ new Map();
  const links = /* @__PURE__ */ new Map();
  const jsonLd = /* @__PURE__ */ new Map();
  return {
    add(tags) {
      if (tags.lang) lang = tags.lang;
      if (tags.title) title = tags.title;
      for (const m of tags.meta ?? []) meta.set(metaKey(m), m);
      for (const l of tags.links ?? []) links.set(linkKey(l), l);
      for (const j of tags.jsonLd ?? []) jsonLd.set(j.key, j);
    },
    snapshot: () => ({
      lang,
      title,
      meta: [...meta.values()],
      links: [...links.values()],
      jsonLd: [...jsonLd.values()]
    })
  };
}
function upsert(tag, match) {
  const selector = tag + Object.entries(match).map(([k, v]) => `[${k}="${v}"]`).join("");
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    for (const [k, v] of Object.entries(match)) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  return el;
}
function applyHeadTags(tags) {
  const nodes = [];
  if (tags.lang) document.documentElement.lang = tags.lang;
  for (const m of tags.meta ?? []) {
    const el = upsert("meta", m.name !== void 0 ? { name: m.name } : { property: m.property });
    el.setAttribute("content", m.content);
    nodes.push(el);
  }
  for (const l of tags.links ?? []) {
    const el = upsert("link", { rel: l.rel, ...l.hreflang ? { hreflang: l.hreflang } : {} });
    el.setAttribute("href", l.href);
    nodes.push(el);
  }
  for (const j of tags.jsonLd ?? []) {
    const el = upsert("script", { type: "application/ld+json", "data-head-key": j.key });
    el.textContent = JSON.stringify(j.data);
    nodes.push(el);
  }
  return () => {
    for (const el of nodes) el.remove();
  };
}

// src/common/SeoHead.tsx
import { useContext as useContext2, useEffect as useEffect4, useMemo } from "react";
import { useLocation } from "react-router-dom";

// src/config/LandingProvider.tsx
import { createContext as createContext2, useContext, useEffect as useEffect2 } from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var BrandConfigContext = createContext2(null);
function isTestEnvironment() {
  if (import.meta.env.MODE === "test") return true;
  if (typeof window !== "undefined" && !!window.__vitest__) {
    return true;
  }
  return false;
}
function buildGtmScript(gtmId) {
  const script = document.createElement("script");
  script.setAttribute("data-gtm", gtmId);
  script.textContent = [
    `;(function(w,d,s,l,i){`,
    `w[l]=w[l]||[];`,
    `w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});`,
    `var f=d.getElementsByTagName(s)[0],`,
    `j=d.createElement(s),`,
    `dl=l!='dataLayer'?'&l='+l:'';`,
    `j.async=true;`,
    `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;`,
    `f.parentNode.insertBefore(j,f);`,
    `})(window,document,'script','dataLayer','${gtmId}');`
  ].join("");
  return script;
}
function LandingProvider({ config, children }) {
  const gtmId = config.gtm?.id;
  useEffect2(() => {
    if (!gtmId || isTestEnvironment()) return;
    const script = buildGtmScript(gtmId);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [gtmId]);
  return /* @__PURE__ */ jsx3(BrandConfigContext.Provider, { value: config, children });
}
function useBrandConfig() {
  const ctx = useContext(BrandConfigContext);
  if (ctx === null) {
    throw new Error("useBrandConfig must be used within a LandingProvider");
  }
  return ctx;
}

// src/common/StructuredData.tsx
import { useEffect as useEffect3 } from "react";
function StructuredData({ data }) {
  useEffect3(() => {
    const items = Array.isArray(data) ? data : [data];
    const scripts = [];
    for (const item of items) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
      scripts.push(script);
    }
    return () => {
      for (const script of scripts) {
        script.remove();
      }
    };
  }, [data]);
  return null;
}
function organizationSchema(config) {
  const sd = config.structuredData;
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: config.name,
    url: config.baseUrl,
    description: sd.description,
    logo: sd.logo ?? `${config.baseUrl}/favicon.svg`,
    ...sd.contactEmail ? {
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: sd.contactEmail,
        availableLanguage: ["German", "English"]
      }
    } : {},
    ...sd.sameAs ? { sameAs: sd.sameAs } : {},
    ...sd.foundingLocation ? {
      foundingLocation: {
        "@type": "Place",
        name: sd.foundingLocation
      }
    } : {},
    ...sd.knowsAbout ? { knowsAbout: sd.knowsAbout } : {}
  };
}
function websiteSchema(config) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: config.name,
    url: config.baseUrl
  };
}
function breadcrumbSchema(baseUrl, items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@id": `${baseUrl}${item.url}`,
        name: item.name
      }
    }))
  };
}

// src/common/SeoHead.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
function seoHeadTags(config, pathname) {
  const langMatch = pathname.match(/^\/(de|en)(?=\/|$)/);
  const lang = langMatch ? langMatch[1] : "de";
  const pathWithoutLang = pathname.replace(/^\/(de|en)(?=\/|$)/, "") || "/";
  const canonicalPath = lang === "en" ? pathWithoutLang === "/" ? "/en" : `/en${pathWithoutLang}` : pathWithoutLang === "/" ? "/de" : `/de${pathWithoutLang}`;
  const dePath = pathWithoutLang === "/" ? "/de" : `/de${pathWithoutLang}`;
  const enPath = pathWithoutLang === "/" ? "/en" : `/en${pathWithoutLang}`;
  const isLegalPage = /\/(impressum|datenschutz|imprint|privacy)/.test(pathname);
  const isHomepage = /^\/(de|en)\/?$/.test(pathname) || /^\/?$/.test(pathname);
  return {
    lang,
    meta: [
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${config.baseUrl}${canonicalPath}` },
      { property: "og:locale", content: lang === "de" ? "de_DE" : "en_US" },
      { property: "og:site_name", content: config.siteName }
    ],
    links: [
      { rel: "canonical", href: `${config.baseUrl}${canonicalPath}` },
      { rel: "alternate", hreflang: "de", href: `${config.baseUrl}${dePath}` },
      { rel: "alternate", hreflang: "en", href: `${config.baseUrl}${enPath}` },
      { rel: "alternate", hreflang: "x-default", href: `${config.baseUrl}${dePath}` }
    ],
    jsonLd: [
      ...isLegalPage ? [] : [{ key: "organization", data: organizationSchema(config) }],
      ...isHomepage ? [{ key: "website", data: websiteSchema(config) }] : []
    ]
  };
}
function SeoHeadContent() {
  const config = useBrandConfig();
  const { pathname } = useLocation();
  const collector = useContext2(HeadCollectorContext);
  const tags = useMemo(() => seoHeadTags(config, pathname), [config, pathname]);
  collector?.add(tags);
  useEffect4(() => applyHeadTags(tags), [tags]);
  return null;
}
function SeoHead() {
  const config = useContext2(BrandConfigContext);
  if (!config) return null;
  return /* @__PURE__ */ jsx4(SeoHeadContent, {});
}

// src/common/SmoothScrollLink.tsx
import { useCallback as useCallback2, useMemo as useMemo2 } from "react";
import { Link, useLocation as useLocation2 } from "react-router-dom";
import { jsx as jsx5 } from "react/jsx-runtime";
function SmoothScrollLink({
  to,
  children,
  onClick,
  ...props
}) {
  const location = useLocation2();
  const langPrefix = location.pathname.startsWith("/en") ? "/en" : "/de";
  const effectiveTo = useMemo2(() => {
    if (to.startsWith("/#")) {
      return `${langPrefix}${to.slice(1)}`;
    }
    return to;
  }, [to, langPrefix]);
  const handleClick = useCallback2(
    (e) => {
      onClick?.(e);
      const isHashLink = to.startsWith("/#") || to.startsWith("#");
      const hash = to.includes("#") ? to.split("#")[1] : null;
      if (isHashLink && hash) {
        const isHomePage = /^\/(de|en)$/.test(location.pathname);
        const isOnSamePage = isHomePage || !to.startsWith("/#") && location.pathname === to.split("#")[0];
        if (isOnSamePage) {
          e.preventDefault();
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
            window.history.pushState(null, "", `#${hash}`);
          }
        }
      }
    },
    [to, location.pathname, onClick]
  );
  return /* @__PURE__ */ jsx5(Link, { to: effectiveTo, onClick: handleClick, ...props, children });
}

// src/hooks/useScrollAnimation.ts
import { useEffect as useEffect5, useLayoutEffect, useRef as useRef2, useState } from "react";
var useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect5 : useLayoutEffect;
function useScrollAnimation(options = {}) {
  const { threshold = 0.1, rootMargin = "0px 0px -50px 0px", triggerOnce = true } = options;
  const ref = useRef2(null);
  const [isVisible, setIsVisible] = useState(true);
  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (element.getBoundingClientRect().top >= window.innerHeight) setIsVisible(false);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);
  return { ref, isVisible };
}

// src/components/AnimatedSection.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function AnimatedSection({
  children,
  className = "",
  delay = 0
}) {
  const { ref, isVisible } = useScrollAnimation();
  return /* @__PURE__ */ jsx6(
    "div",
    {
      ref,
      className: `transition-all duration-700 ease-out ${className}`,
      style: {
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0px)" : "translateY(30px)",
        transitionDelay: `${delay}ms`
      },
      children
    }
  );
}

// src/components/BentoGrid.tsx
import { Fragment, jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var GRADIENT_CLASS = {
  blue: "bg-soft-blue",
  purple: "bg-soft-purple",
  pink: "bg-soft-pink",
  orange: "bg-soft-orange",
  teal: "bg-soft-teal",
  green: "bg-soft-green",
  slate: "bg-[#eef1f6]"
};
var ICON_BG = {
  blue: "bg-blue-100 text-blue-600",
  purple: "bg-purple-100 text-purple-600",
  pink: "bg-pink-100 text-pink-600",
  orange: "bg-orange-100 text-orange-600",
  teal: "bg-teal-100 text-teal-600",
  green: "bg-green-100 text-green-600",
  slate: "bg-slate-200 text-slate-600"
};
function ArrowRight() {
  return /* @__PURE__ */ jsx7("svg", { className: "h-4 w-4", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx7("path", { d: "M5 12H19M19 12L12 5M19 12L12 19", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function BentoGrid({
  label,
  heading,
  headingHighlight,
  description,
  cards,
  id,
  className = ""
}) {
  return /* @__PURE__ */ jsx7("section", { id, "data-nav-theme": "light", className: `bg-[#f2f5fa] py-20 md:py-28 ${className}`, children: /* @__PURE__ */ jsxs3("div", { className: "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8", children: [
    (heading || headingHighlight || label) && /* @__PURE__ */ jsx7(AnimatedSection, { children: /* @__PURE__ */ jsxs3("div", { className: "mx-auto mb-14 max-w-3xl text-center", children: [
      label && /* @__PURE__ */ jsx7("p", { className: "section-label", children: label }),
      (heading || headingHighlight) && /* @__PURE__ */ jsxs3("h2", { className: "section-title", children: [
        heading && /* @__PURE__ */ jsxs3(Fragment, { children: [
          heading,
          " "
        ] }),
        headingHighlight && /* @__PURE__ */ jsx7("span", { className: "text-gradient", children: headingHighlight })
      ] }),
      description && /* @__PURE__ */ jsx7("p", { className: "section-subtitle mx-auto", children: description })
    ] }) }),
    /* @__PURE__ */ jsx7("div", { className: "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4", children: cards.map((card, i) => {
      const isLarge = card.size === "large";
      const isWide = card.size === "wide";
      return /* @__PURE__ */ jsx7(
        AnimatedSection,
        {
          delay: i * 80,
          className: `${isLarge ? "lg:col-span-2 lg:row-span-2" : ""} ${isWide ? "lg:col-span-2" : ""}`,
          children: /* @__PURE__ */ jsxs3(
            "div",
            {
              className: `group flex h-full flex-col ${GRADIENT_CLASS[card.color]} rounded-[18px] p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-soft-lg md:p-8 ${isLarge ? "md:p-10" : ""}`,
              children: [
                typeof card.icon === "string" ? /* @__PURE__ */ jsx7("span", { className: `mb-5 ${isLarge ? "text-5xl" : "text-4xl"}`, children: card.icon }) : /* @__PURE__ */ jsx7(
                  "div",
                  {
                    className: `mb-5 flex items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110 ${ICON_BG[card.color]} ${isLarge ? "h-14 w-14" : "h-12 w-12"}`,
                    children: card.icon
                  }
                ),
                /* @__PURE__ */ jsx7(
                  "h3",
                  {
                    className: `mb-3 font-bold text-gray-900 ${isLarge ? "text-2xl md:text-3xl" : "text-[1.25rem]"}`,
                    children: card.title
                  }
                ),
                /* @__PURE__ */ jsx7(
                  "p",
                  {
                    className: `leading-relaxed text-gray-600 ${isLarge ? "text-lg" : "text-[0.94rem]"}`,
                    children: card.description
                  }
                ),
                card.cta && /* @__PURE__ */ jsx7("div", { className: "mt-auto pt-6", children: card.ctaHref ? /* @__PURE__ */ jsxs3(
                  "a",
                  {
                    href: card.ctaHref,
                    className: "inline-flex items-center gap-2 font-semibold text-sky-600 transition-all duration-200 hover:gap-3",
                    children: [
                      card.cta,
                      " ",
                      /* @__PURE__ */ jsx7(ArrowRight, {})
                    ]
                  }
                ) : /* @__PURE__ */ jsxs3("span", { className: "inline-flex items-center gap-2 font-semibold text-sky-600", children: [
                  card.cta,
                  " ",
                  /* @__PURE__ */ jsx7(ArrowRight, {})
                ] }) })
              ]
            }
          )
        },
        card.title
      );
    }) })
  ] }) });
}

// src/components/Button.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
function Button({
  children,
  variant = "primary",
  fullWidth = false,
  className = "",
  ...props
}) {
  const baseStyles = "font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
  const variantStyles = {
    primary: "bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl",
    secondary: "bg-white hover:bg-gray-50 text-primary-700 border-2 border-primary-600"
  };
  const widthClass = fullWidth ? "w-full" : "";
  return /* @__PURE__ */ jsx8(
    "button",
    {
      className: `${baseStyles} ${variantStyles[variant]} ${widthClass} ${className}`,
      ...props,
      children
    }
  );
}

// src/components/Container.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
function Container({ children, className = "", maxWidth = "xl" }) {
  const maxWidthClasses = {
    sm: "max-w-screen-sm",
    md: "max-w-screen-md",
    lg: "max-w-screen-lg",
    xl: "max-w-screen-xl",
    "2xl": "max-w-screen-2xl",
    full: "max-w-full"
  };
  return /* @__PURE__ */ jsx9("div", { className: `mx-auto px-4 sm:px-6 lg:px-8 ${maxWidthClasses[maxWidth]} ${className}`, children });
}

// src/components/ContentSection.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
function ContentSection({
  children,
  id,
  className = "",
  containerClassName = "",
  maxWidth = "xl",
  background = "white"
}) {
  const backgroundClasses = {
    white: "bg-white",
    gray: "bg-gray-50",
    gradient: "bg-gradient-to-b from-white to-gray-50"
  };
  return /* @__PURE__ */ jsx10("section", { id, className: `py-16 md:py-24 ${backgroundClasses[background]} ${className}`, children: /* @__PURE__ */ jsx10(Container, { maxWidth, className: containerClassName, children }) });
}

// src/components/CTA.tsx
import { jsx as jsx11, jsxs as jsxs4 } from "react/jsx-runtime";
function CTA({
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  variant = "default",
  className = ""
}) {
  const variants = {
    default: "bg-muted-50",
    gradient: "bg-gradient-to-r from-primary-500 to-primary-600",
    dark: "bg-gray-900"
  };
  const textColor = variant === "default" ? "text-gray-900" : "text-white";
  const subtitleColor = variant === "default" ? "text-gray-600" : "text-white/80";
  return /* @__PURE__ */ jsx11("section", { className: `py-20 md:py-28 ${variants[variant]} ${className}`, children: /* @__PURE__ */ jsx11("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs4("div", { className: "max-w-3xl mx-auto text-center", children: [
    /* @__PURE__ */ jsx11("h2", { className: `text-3xl md:text-4xl lg:text-5xl font-bold mb-4 ${textColor}`, children: title }),
    subtitle && /* @__PURE__ */ jsx11("p", { className: `text-lg mb-8 ${subtitleColor}`, children: subtitle }),
    /* @__PURE__ */ jsxs4("div", { className: "flex flex-col sm:flex-row gap-4 justify-center", children: [
      /* @__PURE__ */ jsxs4(
        "a",
        {
          href: primaryCta.href,
          className: `inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold transition-colors ${variant === "default" ? "bg-primary-500 text-white hover:bg-primary-600" : "bg-white text-primary-600 hover:bg-gray-100"}`,
          children: [
            primaryCta.text,
            /* @__PURE__ */ jsx11(
              "svg",
              {
                className: "w-4 h-4",
                viewBox: "0 0 24 24",
                fill: "none",
                xmlns: "http://www.w3.org/2000/svg",
                children: /* @__PURE__ */ jsx11(
                  "path",
                  {
                    d: "M5 12H19M19 12L12 5M19 12L12 19",
                    stroke: "currentColor",
                    strokeWidth: "2",
                    strokeLinecap: "round",
                    strokeLinejoin: "round"
                  }
                )
              }
            )
          ]
        }
      ),
      secondaryCta && /* @__PURE__ */ jsx11(
        "a",
        {
          href: secondaryCta.href,
          className: `inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold transition-colors ${variant === "default" ? "border border-gray-300 text-gray-700 hover:bg-gray-50" : "border border-white/30 text-white hover:bg-white/10"}`,
          children: secondaryCta.text
        }
      )
    ] })
  ] }) }) });
}

// src/components/DarkCard.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
function DarkCard({ children, className = "" }) {
  return /* @__PURE__ */ jsx12(
    "div",
    {
      className: `df-dark-card relative overflow-hidden rounded-[24px] border border-[var(--df-line-soft)] bg-[linear-gradient(180deg,rgba(17,30,54,0.95)_0%,rgba(11,20,37,0.96)_100%)] p-5 shadow-[var(--df-shadow-card)] sm:p-8 ${className}`,
      children: /* @__PURE__ */ jsx12("div", { className: "relative z-10", children })
    }
  );
}

// src/components/FAQ.tsx
import { jsx as jsx13, jsxs as jsxs5 } from "react/jsx-runtime";
function FAQ({
  label,
  title,
  subtitle,
  items,
  ctaText,
  ctaHref,
  ctaLabel,
  className = ""
}) {
  return /* @__PURE__ */ jsx13("section", { className: `py-20 md:py-28 bg-muted-50 ${className}`, children: /* @__PURE__ */ jsxs5("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs5("div", { className: "text-center max-w-3xl mx-auto mb-16", children: [
      label && /* @__PURE__ */ jsx13("p", { className: "inline-flex items-center px-4 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium border border-primary-200 mb-4", children: label }),
      /* @__PURE__ */ jsx13("h2", { className: "text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4", children: title }),
      subtitle && /* @__PURE__ */ jsx13("p", { className: "text-lg text-gray-600 max-w-2xl mx-auto", children: subtitle })
    ] }),
    /* @__PURE__ */ jsx13("div", { className: "max-w-4xl mx-auto", children: /* @__PURE__ */ jsx13("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-8", children: items.map((faq, index) => /* @__PURE__ */ jsxs5("div", { className: "bg-white rounded-2xl p-6 shadow-soft", children: [
      /* @__PURE__ */ jsx13("h3", { className: "text-lg font-bold text-gray-900 mb-3", children: faq.question }),
      /* @__PURE__ */ jsx13("p", { className: "text-gray-600 leading-relaxed", children: faq.answer })
    ] }, index)) }) }),
    ctaText && ctaHref && /* @__PURE__ */ jsxs5("div", { className: "text-center mt-12", children: [
      ctaLabel && /* @__PURE__ */ jsx13("p", { className: "text-gray-600 mb-4", children: ctaLabel }),
      /* @__PURE__ */ jsx13(
        "a",
        {
          href: ctaHref,
          className: "inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors",
          children: ctaText
        }
      )
    ] })
  ] }) });
}

// src/components/FeatureCard.tsx
import { jsx as jsx14, jsxs as jsxs6 } from "react/jsx-runtime";
function FeatureCard({ title, items, icon, className = "" }) {
  return /* @__PURE__ */ jsxs6(
    "div",
    {
      className: `flex min-h-[280px] flex-col justify-between overflow-hidden rounded-[18px] border border-[#dce2eb] bg-[#eef1f6] p-6 sm:p-8 ${className}`,
      children: [
        /* @__PURE__ */ jsxs6("div", { children: [
          icon && /* @__PURE__ */ jsx14("span", { className: "mb-4 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-cyan-400 text-white shadow-[0_2px_8px_rgba(45,212,191,0.3)] sm:h-10 sm:w-10", children: icon }),
          /* @__PURE__ */ jsx14(
            "h3",
            {
              className: "text-[1.25rem] font-semibold leading-[1.2] tracking-tight text-slate-900 sm:text-[1.45rem] break-words hyphens-auto",
              lang: "de",
              children: title
            }
          )
        ] }),
        /* @__PURE__ */ jsx14("ul", { className: "mt-auto space-y-2 pt-6", children: items.map((item) => /* @__PURE__ */ jsxs6("li", { className: "flex items-start gap-2.5 text-[0.94rem] text-slate-700", children: [
          /* @__PURE__ */ jsx14(
            "span",
            {
              "aria-hidden": "true",
              className: "mt-[7px] h-2 w-2 shrink-0 rounded-full bg-teal-400"
            }
          ),
          /* @__PURE__ */ jsx14("span", { children: item })
        ] }, item)) })
      ]
    }
  );
}

// src/components/Features.tsx
import { jsx as jsx15, jsxs as jsxs7 } from "react/jsx-runtime";
function Features({
  label,
  title,
  subtitle,
  features,
  columns = 3,
  className = ""
}) {
  const gridCols = {
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-2 lg:grid-cols-4"
  };
  return /* @__PURE__ */ jsx15("section", { className: `py-20 md:py-28 bg-white ${className}`, children: /* @__PURE__ */ jsxs7("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs7("div", { className: "text-center max-w-3xl mx-auto mb-16", children: [
      label && /* @__PURE__ */ jsx15("p", { className: "inline-flex items-center px-4 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium border border-primary-200 mb-4", children: label }),
      /* @__PURE__ */ jsx15("h2", { className: "text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4", children: title }),
      subtitle && /* @__PURE__ */ jsx15("p", { className: "text-lg text-gray-600 max-w-2xl mx-auto", children: subtitle })
    ] }),
    /* @__PURE__ */ jsx15("div", { className: `grid grid-cols-1 ${gridCols[columns]} gap-8`, children: features.map((feature, index) => /* @__PURE__ */ jsxs7(
      "div",
      {
        className: "bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-soft-lg transition-shadow duration-300",
        children: [
          /* @__PURE__ */ jsx15("div", { className: "w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center mb-6 text-primary-500", children: feature.icon }),
          /* @__PURE__ */ jsx15("h3", { className: "text-xl font-bold text-gray-900 mb-3", children: feature.title }),
          /* @__PURE__ */ jsx15("p", { className: "text-gray-600 leading-relaxed", children: feature.description })
        ]
      },
      index
    )) })
  ] }) });
}

// src/components/IntegrationFunnel/IntegrationFunnel.tsx
import { useCallback as useCallback3, useEffect as useEffect6, useMemo as useMemo3, useRef as useRef3, useState as useState2 } from "react";

// src/components/IntegrationFunnel/selectors.ts
var bySlug = (catalogue, slug) => catalogue.systems.find((s) => s.slug === slug);
function systemsForRoles(catalogue, roles) {
  return catalogue.systems.filter((s) => roles.includes(s.role));
}
function availableFlows(catalogue, answers) {
  const hasCustom = Boolean(answers.sourceCustom?.trim() || answers.targetCustom?.trim());
  if (hasCustom) return catalogue.flows;
  const chosen = [];
  if (answers.sourceSlug) {
    const s = bySlug(catalogue, answers.sourceSlug);
    if (s) chosen.push(s);
  }
  for (const slug of answers.targetSlugs) {
    const s = bySlug(catalogue, slug);
    if (s) chosen.push(s);
  }
  if (chosen.length === 0) return catalogue.flows;
  return catalogue.flows.filter((flow) => chosen.every((s) => s.flows.includes(flow.id)));
}
function flowOptions(catalogue, answers) {
  const standard = new Set(availableFlows(catalogue, answers).map((f) => f.id));
  return catalogue.flows.map((flow) => ({ flow, standard: standard.has(flow.id) }));
}
function nonStandardPicks(catalogue, answers) {
  const standard = new Set(availableFlows(catalogue, answers).map((f) => f.id));
  return catalogue.flows.filter(
    (f) => answers.flowIds.includes(f.id) && !standard.has(f.id)
  );
}
function isStepAnswered(answers, step) {
  switch (step) {
    case 1:
      return Boolean(answers.sourceSlug || answers.sourceCustom?.trim());
    case 2:
      return answers.targetSlugs.length > 0 || Boolean(answers.targetCustom?.trim());
    case 3:
      return answers.flowIds.length > 0 || Boolean(answers.flowCustom?.trim());
    case 4:
      return Boolean(answers.scaleId);
  }
}
function isComplete(answers) {
  return [1, 2, 3, 4].every((s) => isStepAnswered(answers, s));
}
function firstOpenStep(answers) {
  for (const step of [1, 2, 3, 4]) {
    if (!isStepAnswered(answers, step)) return step;
  }
  return null;
}
function buildSummary(config, answers) {
  const { catalogue } = config;
  const name = (slug) => bySlug(catalogue, slug)?.name ?? slug;
  const nonStandard = new Set(nonStandardPicks(catalogue, answers).map((f) => f.label));
  const custom = [answers.sourceCustom, answers.targetCustom, answers.flowCustom].map((c) => c?.trim()).filter((c) => Boolean(c));
  const flows = answers.flowIds.map((id) => catalogue.flows.find((f) => f.id === id)?.label).filter((l) => Boolean(l));
  return {
    source: answers.sourceSlug ? name(answers.sourceSlug) : answers.sourceCustom?.trim(),
    targets: answers.targetSlugs.map(name),
    flows,
    nonStandardFlows: flows.filter((l) => nonStandard.has(l)),
    scale: config.scaleOptions.find((v) => v.id === answers.scaleId)?.label,
    custom
  };
}

// src/components/IntegrationFunnel/types.ts
var emptyAnswers = () => ({
  targetSlugs: [],
  flowIds: []
});

// src/components/IntegrationFunnel/IntegrationFunnel.tsx
import { jsx as jsx16, jsxs as jsxs8 } from "react/jsx-runtime";
var TOTAL_STEPS = 4;
function Check() {
  return /* @__PURE__ */ jsx16("svg", { viewBox: "0 0 20 20", fill: "currentColor", "aria-hidden": "true", className: "h-4 w-4 shrink-0", children: /* @__PURE__ */ jsx16(
    "path",
    {
      fillRule: "evenodd",
      d: "M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 111.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z",
      clipRule: "evenodd"
    }
  ) });
}
function Option({
  label,
  selected,
  onClick,
  badge,
  muted = false
}) {
  return /* @__PURE__ */ jsxs8(
    "button",
    {
      type: "button",
      onClick,
      "aria-pressed": selected,
      className: [
        "group flex items-center justify-between gap-3 rounded-xl border px-4 py-3.5 text-left text-sm transition-all duration-200",
        selected ? "border-primary-600 bg-primary-600 font-semibold text-white shadow-md" : muted ? "border-dashed border-slate-300 bg-white/60 font-medium text-slate-400 hover:border-primary-600 hover:bg-white hover:text-slate-700" : "border-[#dce2eb] bg-white font-medium text-slate-700 hover:border-primary-600 hover:shadow-sm"
      ].join(" "),
      children: [
        /* @__PURE__ */ jsxs8("span", { children: [
          label,
          badge && /* @__PURE__ */ jsxs8("span", { className: "sr-only", children: [
            " \u2014 ",
            badge
          ] })
        ] }),
        /* @__PURE__ */ jsx16(
          "span",
          {
            className: selected ? "text-white" : "text-transparent transition-colors group-hover:text-slate-300",
            children: /* @__PURE__ */ jsx16(Check, {})
          }
        )
      ]
    }
  );
}
function CustomField({
  label,
  placeholder,
  value,
  onChange
}) {
  return /* @__PURE__ */ jsxs8("label", { className: "mt-4 block", children: [
    /* @__PURE__ */ jsx16("span", { className: "mb-2 block text-sm font-medium text-slate-500", children: label }),
    /* @__PURE__ */ jsx16(
      "textarea",
      {
        rows: 2,
        value,
        placeholder,
        onChange: (e) => onChange(e.target.value),
        className: "w-full resize-y rounded-xl border border-[#dce2eb] bg-white px-4 py-3 text-sm text-slate-800 transition-colors duration-200 placeholder:text-slate-300 focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/10"
      }
    )
  ] });
}
function ContactStep({
  contact,
  summary,
  answers
}) {
  const [state, setState] = useState2("idle");
  const mountedAt = useRef3(Date.now());
  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    const data = new FormData(e.currentTarget);
    const read = (name) => {
      const v = data.get(name);
      return typeof v === "string" ? v : "";
    };
    const values = {};
    for (const f of contact.fields) values[f.name] = read(f.name);
    if (contact.honeypotField) values[contact.honeypotField] = read(contact.honeypotField);
    if (contact.fields.some((f) => f.required && !values[f.name].trim())) {
      setState("error");
      return;
    }
    setState("sending");
    try {
      await contact.onSubmit(
        { ...values, elapsedMs: Date.now() - mountedAt.current },
        summary,
        answers
      );
      setState("sent");
    } catch {
      setState("error");
    }
  };
  if (state === "sent") {
    return /* @__PURE__ */ jsxs8(
      "div",
      {
        "data-testid": "funnel-contact-sent",
        className: "mt-7 rounded-xl border border-emerald-200 bg-emerald-50 p-5",
        children: [
          /* @__PURE__ */ jsx16("p", { className: "font-semibold text-emerald-900", children: contact.successTitle }),
          /* @__PURE__ */ jsx16("p", { className: "mt-1 text-sm leading-relaxed text-emerald-800", children: contact.successBody })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs8("form", { onSubmit: submit, "data-testid": "funnel-contact", className: "mt-7", children: [
    /* @__PURE__ */ jsx16("p", { className: "text-sm font-semibold text-slate-900", children: contact.title }),
    contact.hint && /* @__PURE__ */ jsx16("p", { className: "mt-1 text-sm text-slate-500", children: contact.hint }),
    /* @__PURE__ */ jsx16("div", { className: "mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2", children: contact.fields.map((f) => /* @__PURE__ */ jsxs8("label", { className: f.name === "message" ? "sm:col-span-2" : void 0, children: [
      /* @__PURE__ */ jsxs8("span", { className: "mb-1.5 block text-xs font-medium text-slate-500", children: [
        f.label,
        f.required && /* @__PURE__ */ jsx16("span", { "aria-hidden": "true", children: " *" })
      ] }),
      /* @__PURE__ */ jsx16(
        "input",
        {
          type: f.type ?? "text",
          name: f.name,
          required: f.required,
          autoComplete: f.autoComplete,
          placeholder: f.placeholder,
          className: "w-full rounded-xl border border-[#dce2eb] bg-white px-4 py-2.5 text-sm text-slate-800 transition-colors placeholder:text-slate-300 focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/10"
        }
      )
    ] }, f.name)) }),
    contact.honeypotField && // Offscreen rather than display:none — bots fill what is in the DOM, and
    // aria-hidden + tabIndex keep it away from humans and screen readers.
    /* @__PURE__ */ jsx16("div", { "aria-hidden": "true", className: "absolute left-[-9999px] top-auto h-px w-px overflow-hidden", children: /* @__PURE__ */ jsx16(
      "input",
      {
        type: "text",
        name: contact.honeypotField,
        tabIndex: -1,
        autoComplete: "off"
      }
    ) }),
    state === "error" && /* @__PURE__ */ jsx16("p", { role: "alert", className: "mt-3 text-sm text-red-600", children: contact.errorBody }),
    /* @__PURE__ */ jsx16(
      "button",
      {
        type: "submit",
        disabled: state === "sending",
        className: "mt-4 w-full rounded-lg bg-primary-600 px-6 py-3 font-semibold text-white shadow-md transition-all duration-200 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto",
        children: state === "sending" ? contact.submittingLabel : contact.submitLabel
      }
    )
  ] });
}
function IntegrationFunnel({
  config,
  labels,
  resolveOutcome,
  actions,
  contact,
  initial,
  presentation = "inline",
  open = true,
  onClose,
  onComplete,
  className = ""
}) {
  const [answers, setAnswers] = useState2({ ...emptyAnswers(), ...initial });
  const [step, setStep] = useState2(() => firstOpenStep({ ...emptyAnswers(), ...initial }) ?? 1);
  const completedRef = useRef3(false);
  const sources = useMemo3(
    () => systemsForRoles(config.catalogue, config.sourceRoles),
    [config]
  );
  const targets = useMemo3(
    () => systemsForRoles(config.catalogue, config.targetRoles),
    [config]
  );
  const flows = useMemo3(() => flowOptions(config.catalogue, answers), [config, answers]);
  const hasNonStandard = flows.some((f) => !f.standard);
  const summary = useMemo3(() => buildSummary(config, answers), [config, answers]);
  const done = isComplete(answers);
  const outcome = useMemo3(
    () => done ? resolveOutcome(answers, summary) : null,
    [done, answers, summary, resolveOutcome]
  );
  useEffect6(() => {
    if (done && outcome && !completedRef.current) {
      completedRef.current = true;
      onComplete?.(answers, summary, outcome);
    }
  }, [done, outcome, answers, summary, onComplete]);
  const update = useCallback3(
    (patch) => setAnswers((prev) => ({ ...prev, ...patch })),
    []
  );
  const toggle = (list, value) => list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  const advance = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  useEffect6(() => {
    if (presentation !== "modal" || !open || !onClose) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [presentation, open, onClose]);
  const restart = () => {
    completedRef.current = false;
    setAnswers({ ...emptyAnswers(), ...initial });
    setStep(firstOpenStep({ ...emptyAnswers(), ...initial }) ?? 1);
  };
  const renderResult = (out) => /* @__PURE__ */ jsxs8("div", { "data-testid": "funnel-result", children: [
    /* @__PURE__ */ jsx16("p", { className: "mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-sky-500", children: labels.result.title }),
    /* @__PURE__ */ jsx16("p", { className: "text-3xl font-bold tracking-tight text-slate-900", children: out.headline }),
    out.subline && /* @__PURE__ */ jsx16("p", { className: "mt-2 text-lg text-slate-500", children: out.subline }),
    out.details && out.details.length > 0 && /* @__PURE__ */ jsx16("ul", { className: "mt-5 space-y-2", children: out.details.map((d) => /* @__PURE__ */ jsxs8("li", { className: "flex gap-2.5 text-sm leading-relaxed text-slate-600", children: [
      /* @__PURE__ */ jsx16("span", { "aria-hidden": "true", className: "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" }),
      d
    ] }, d)) }),
    /* @__PURE__ */ jsxs8("dl", { className: "mt-7 space-y-2 rounded-xl border border-[#dce2eb] bg-[#f8fafc] p-5 text-sm", children: [
      [
        [labels.result.sourceLabel, summary.source],
        [labels.result.targetsLabel, summary.targets.join(", ")],
        [labels.result.flowsLabel, summary.flows.join(", ")],
        [labels.result.scaleLabel, summary.scale]
      ].filter(([, v]) => Boolean(v)).map(([k, v]) => /* @__PURE__ */ jsxs8("div", { className: "flex gap-3", children: [
        /* @__PURE__ */ jsx16("dt", { className: "w-32 shrink-0 font-medium text-slate-400", children: k }),
        /* @__PURE__ */ jsx16("dd", { className: "text-slate-800", children: v })
      ] }, k)),
      summary.custom.length > 0 && /* @__PURE__ */ jsxs8("div", { className: "flex gap-3", children: [
        /* @__PURE__ */ jsx16("dt", { className: "w-32 shrink-0 font-medium text-slate-400", children: labels.result.notesLabel }),
        /* @__PURE__ */ jsx16("dd", { className: "whitespace-pre-wrap text-slate-800", children: summary.custom.join("\n") })
      ] })
    ] }),
    /* @__PURE__ */ jsx16("div", { className: "mt-6 flex flex-wrap gap-3", children: actions.map((action) => {
      const cls = action.variant === "secondary" ? "rounded-lg border-2 border-primary-600 bg-white px-6 py-3 font-semibold text-primary-700 transition-all duration-200 hover:bg-gray-50" : "rounded-lg bg-primary-600 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:bg-primary-700 hover:shadow-xl";
      const href = typeof action.href === "function" ? action.href(summary, answers) : action.href;
      return href ? /* @__PURE__ */ jsx16(
        "a",
        {
          href,
          className: cls,
          onClick: () => action.onClick?.(summary, answers),
          children: action.label
        },
        action.id
      ) : /* @__PURE__ */ jsx16(
        "button",
        {
          type: "button",
          className: cls,
          onClick: () => action.onClick?.(summary, answers),
          children: action.label
        },
        action.id
      );
    }) }),
    contact && /* @__PURE__ */ jsx16(ContactStep, { contact, summary, answers }),
    /* @__PURE__ */ jsx16(
      "button",
      {
        type: "button",
        onClick: restart,
        className: "mt-5 text-sm font-medium text-slate-400 underline-offset-4 hover:text-slate-600 hover:underline",
        children: labels.result.restart
      }
    )
  ] });
  const renderStep = () => {
    const stepLabels = step === 1 ? labels.source : step === 2 ? labels.target : step === 3 ? labels.flows : null;
    return /* @__PURE__ */ jsxs8("div", { children: [
      /* @__PURE__ */ jsxs8("div", { className: "mb-7 flex items-center gap-4", children: [
        /* @__PURE__ */ jsx16("div", { className: "h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100", children: /* @__PURE__ */ jsx16(
          "div",
          {
            className: "h-full rounded-full bg-primary-600 transition-all duration-300",
            style: { width: `${step / TOTAL_STEPS * 100}%` }
          }
        ) }),
        /* @__PURE__ */ jsx16("span", { className: "shrink-0 text-xs font-semibold uppercase tracking-wider text-slate-400", children: labels.stepIndicator(step, TOTAL_STEPS) })
      ] }),
      /* @__PURE__ */ jsx16("h3", { className: "text-xl font-bold tracking-tight text-slate-900 md:text-2xl", children: step === 4 ? labels.scale.title : stepLabels?.title }),
      (step === 4 ? labels.scale.hint : stepLabels?.hint) && /* @__PURE__ */ jsx16("p", { className: "mt-2.5 text-sm leading-relaxed text-slate-500", children: step === 4 ? labels.scale.hint : stepLabels?.hint }),
      /* @__PURE__ */ jsxs8("div", { className: "mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2", children: [
        step === 1 && sources.map((s) => /* @__PURE__ */ jsx16(
          Option,
          {
            label: s.name,
            selected: answers.sourceSlug === s.slug,
            onClick: () => {
              update({ sourceSlug: s.slug });
              advance();
            }
          },
          s.slug
        )),
        step === 2 && targets.map((s) => /* @__PURE__ */ jsx16(
          Option,
          {
            label: s.name,
            selected: answers.targetSlugs.includes(s.slug),
            onClick: () => update({ targetSlugs: toggle(answers.targetSlugs, s.slug) })
          },
          s.slug
        )),
        step === 3 && flows.map(({ flow, standard }) => /* @__PURE__ */ jsx16(
          Option,
          {
            label: flow.label,
            badge: standard ? void 0 : labels.nonStandardBadge,
            muted: !standard,
            selected: answers.flowIds.includes(flow.id),
            onClick: () => update({ flowIds: toggle(answers.flowIds, flow.id) })
          },
          flow.id
        )),
        step === 4 && config.scaleOptions.map((v) => /* @__PURE__ */ jsx16(
          Option,
          {
            label: v.label,
            selected: answers.scaleId === v.id,
            onClick: () => update({ scaleId: v.id })
          },
          v.id
        ))
      ] }),
      step === 3 && hasNonStandard && /* @__PURE__ */ jsx16("p", { className: "mt-4 text-xs leading-relaxed text-slate-400", children: labels.nonStandardHint }),
      stepLabels && /* @__PURE__ */ jsx16(
        CustomField,
        {
          label: stepLabels.customLabel,
          placeholder: stepLabels.customPlaceholder,
          value: (step === 1 ? answers.sourceCustom : step === 2 ? answers.targetCustom : answers.flowCustom) ?? "",
          onChange: (v) => update(
            step === 1 ? { sourceCustom: v } : step === 2 ? { targetCustom: v } : { flowCustom: v }
          )
        }
      ),
      /* @__PURE__ */ jsxs8("div", { className: "mt-7 flex items-center justify-between gap-3", children: [
        /* @__PURE__ */ jsx16(
          "button",
          {
            type: "button",
            onClick: () => setStep((s) => Math.max(1, s - 1)),
            disabled: step === 1,
            className: "text-sm font-medium text-slate-400 transition-colors duration-200 hover:text-slate-600 disabled:invisible",
            children: labels.back
          }
        ),
        step < TOTAL_STEPS && /* @__PURE__ */ jsx16(
          "button",
          {
            type: "button",
            onClick: advance,
            disabled: !isStepAnswered(answers, step),
            className: "rounded-lg bg-primary-600 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:bg-primary-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50",
            children: labels.continueLabel
          }
        )
      ] })
    ] });
  };
  const body = /* @__PURE__ */ jsx16(
    "div",
    {
      "data-testid": "integration-funnel",
      className: `rounded-[18px] border border-[#dce2eb] bg-white p-6 shadow-sm md:p-9 ${className}`,
      children: done && outcome ? renderResult(outcome) : renderStep()
    }
  );
  if (presentation !== "modal") return body;
  if (!open) return null;
  return /* @__PURE__ */ jsx16(
    "div",
    {
      className: "fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-900/50 p-4 py-10 animate-fade-in",
      onClick: onClose,
      role: "presentation",
      children: /* @__PURE__ */ jsxs8(
        "div",
        {
          role: "dialog",
          "aria-modal": "true",
          "aria-label": labels.result.title,
          className: "w-full max-w-2xl",
          onClick: (e) => e.stopPropagation(),
          children: [
            /* @__PURE__ */ jsx16("div", { className: "mb-2 flex justify-end", children: /* @__PURE__ */ jsx16(
              "button",
              {
                type: "button",
                onClick: onClose,
                className: "rounded-lg bg-white/90 px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-900",
                children: labels.close
              }
            ) }),
            body
          ]
        }
      )
    }
  );
}

// src/components/Footer.tsx
import { Link as Link2 } from "react-router-dom";
import { jsx as jsx17, jsxs as jsxs9 } from "react/jsx-runtime";
function Footer({ copyright, links, attribution, attributionHref }) {
  const attributionClass = "text-sm text-muted transition hover:text-primary";
  return /* @__PURE__ */ jsx17("footer", { className: "border-t border-subtle", children: /* @__PURE__ */ jsx17("div", { className: "mx-auto max-w-7xl px-6 py-8", children: /* @__PURE__ */ jsxs9("div", { className: "flex flex-col items-center justify-between gap-4 md:flex-row", children: [
    /* @__PURE__ */ jsx17("p", { className: "font-mono text-sm text-muted", children: copyright }),
    /* @__PURE__ */ jsxs9("div", { className: "flex flex-wrap items-center gap-4 sm:gap-6", children: [
      links.map(
        (link) => link.external ? /* @__PURE__ */ jsx17(
          "a",
          {
            href: link.href,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "text-sm text-muted transition hover:text-primary",
            children: link.label
          },
          link.href
        ) : /* @__PURE__ */ jsx17(
          Link2,
          {
            to: link.href,
            className: "text-sm text-muted transition hover:text-primary",
            children: link.label
          },
          link.href
        )
      ),
      attribution && (attributionHref ? /* @__PURE__ */ jsx17(
        "a",
        {
          href: attributionHref,
          target: "_blank",
          rel: "noopener noreferrer",
          className: attributionClass,
          children: attribution
        }
      ) : /* @__PURE__ */ jsx17("span", { className: "text-sm text-muted", children: attribution }))
    ] })
  ] }) }) });
}

// src/components/PageTransition.tsx
import { useRef as useRef4 } from "react";
import { useLocation as useLocation3 } from "react-router-dom";
import { jsx as jsx18 } from "react/jsx-runtime";
function PageTransition({ children }) {
  const location = useLocation3();
  const isFirstRender = useRef4(true);
  if (isFirstRender.current) {
    isFirstRender.current = false;
    return /* @__PURE__ */ jsx18("div", { "data-page-transition": true, children });
  }
  return /* @__PURE__ */ jsx18("div", { "data-page-transition": true, className: "animate-fade-in", children }, location.key);
}

// src/components/ScrollToTop.tsx
import { useEffect as useEffect7 } from "react";
import { useLocation as useLocation4 } from "react-router-dom";
function ScrollToTop() {
  const { hash } = useLocation4();
  useEffect7(() => {
    if (hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 150);
      return () => clearTimeout(timeoutId);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [hash]);
  return null;
}

// src/components/Layout.tsx
import { jsx as jsx19, jsxs as jsxs10 } from "react/jsx-runtime";
function Layout({ navbar, footer, seoHead, children }) {
  return /* @__PURE__ */ jsxs10("div", { className: "min-h-screen flex flex-col", children: [
    seoHead ?? /* @__PURE__ */ jsx19(SeoHead, {}),
    /* @__PURE__ */ jsx19(ScrollToTop, {}),
    navbar,
    /* @__PURE__ */ jsx19("main", { className: "relative z-10 flex-grow bg-[#f2f5fa]", children: /* @__PURE__ */ jsx19(PageTransition, { children }) }),
    footer
  ] });
}

// src/components/Navbar.tsx
import { useCallback as useCallback4, useEffect as useEffect8, useRef as useRef5, useState as useState3 } from "react";
import { Link as Link3, useLocation as useLocation5 } from "react-router-dom";
import { Fragment as Fragment2, jsx as jsx20, jsxs as jsxs11 } from "react/jsx-runtime";
function isLanguageLinks(ls) {
  return "dePath" in ls;
}
function isLanguageDropdown(ls) {
  return "options" in ls;
}
function Navbar({
  logo,
  brandName,
  homeLink,
  links,
  dropdown,
  blogPosts,
  blogPostsLabel,
  languageSwitch,
  cta
}) {
  const location = useLocation5();
  const [isMenuOpen, setIsMenuOpen] = useState3(false);
  const [isScrolled, setIsScrolled] = useState3(false);
  const [isLightNav, setIsLightNav] = useState3(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState3(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState3(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState3(false);
  const dropdownTimeoutRef = useRef5(null);
  const dropdownRef = useRef5(null);
  useEffect8(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect8(() => {
    const updateNavTheme = () => {
      const navY = 40;
      const darkSections = document.querySelectorAll("[data-nav-theme='dark']");
      let overDark = false;
      for (const section of darkSections) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= navY && rect.bottom >= navY) {
          overDark = true;
          break;
        }
      }
      setIsLightNav(!overDark);
    };
    updateNavTheme();
    window.addEventListener("scroll", updateNavTheme, { passive: true });
    window.addEventListener("resize", updateNavTheme);
    return () => {
      window.removeEventListener("scroll", updateNavTheme);
      window.removeEventListener("resize", updateNavTheme);
    };
  }, []);
  useEffect8(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);
  useEffect8(() => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    setIsLangDropdownOpen(false);
  }, [location.pathname]);
  const langDropdownRef = useRef5(null);
  useEffect8(() => {
    if (!isLangDropdownOpen) return;
    function handleClick(e) {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setIsLangDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isLangDropdownOpen]);
  const closeMobileMenu = useCallback4(() => setIsMenuOpen(false), []);
  const isActivePath = (path) => location.pathname === path || path !== "/" && location.pathname.startsWith(`${path}/`);
  const handleDropdownEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setIsDropdownOpen(true);
  };
  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => setIsDropdownOpen(false), 150);
  };
  const dropdownPaths = dropdown?.items.map((i) => i.to) ?? [];
  const isDropdownActive = dropdownPaths.some((p) => isActivePath(p));
  const renderDesktopLink = (link) => {
    const isActive = isActivePath(link.to);
    return /* @__PURE__ */ jsxs11(
      Link3,
      {
        to: link.to,
        className: `inline-flex items-center gap-2 text-[14px] font-medium transition-colors duration-500 ${isLightNav ? isActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900" : isActive ? "text-white" : "text-white/90 hover:text-white"}`,
        children: [
          /* @__PURE__ */ jsx20(
            "span",
            {
              "aria-hidden": "true",
              className: `h-1.5 w-1.5 rounded-full transition-all duration-500 ${isLightNav ? isActive ? "bg-sky-500 opacity-100" : "bg-slate-300 opacity-0" : isActive ? "bg-sky-300 opacity-100" : "bg-sky-200/60 opacity-0"}`
            }
          ),
          /* @__PURE__ */ jsx20("span", { children: link.label })
        ]
      },
      link.to
    );
  };
  const logoWidth = logo?.width ?? 180;
  const renderLogo = (size) => {
    if (!logo) {
      return /* @__PURE__ */ jsx20("span", { className: `font-bold transition-colors duration-500 ${size === "lg" ? "text-xl" : "text-lg"} ${isLightNav ? "text-slate-900" : "text-white"}`, children: brandName });
    }
    if (size === "lg") {
      return /* @__PURE__ */ jsxs11(Fragment2, { children: [
        /* @__PURE__ */ jsx20(
          "img",
          {
            src: logo.light,
            alt: logo.alt,
            className: `absolute inset-0 h-10 w-auto transition-opacity duration-500 ${isLightNav ? "opacity-100" : "opacity-0"}`,
            loading: "eager"
          }
        ),
        /* @__PURE__ */ jsx20(
          "img",
          {
            src: logo.dark,
            alt: "",
            "aria-hidden": "true",
            className: `absolute inset-0 h-10 w-auto transition-opacity duration-500 ${isLightNav ? "opacity-0" : "opacity-100"}`,
            loading: "eager"
          }
        )
      ] });
    }
    return /* @__PURE__ */ jsxs11(Fragment2, { children: [
      /* @__PURE__ */ jsx20(
        "img",
        {
          src: logo.light,
          alt: logo.alt,
          className: `absolute inset-0 h-8 w-auto transition-opacity duration-500 sm:h-9 ${isLightNav ? "opacity-100" : "opacity-0"}`,
          loading: "eager"
        }
      ),
      /* @__PURE__ */ jsx20(
        "img",
        {
          src: logo.dark,
          alt: "",
          "aria-hidden": "true",
          className: `absolute inset-0 h-8 w-auto transition-opacity duration-500 sm:h-9 ${isLightNav ? "opacity-0" : "opacity-100"}`,
          loading: "eager"
        }
      )
    ] });
  };
  const renderDesktopLanguageSwitch = () => {
    if (!languageSwitch) return null;
    if (isLanguageLinks(languageSwitch)) {
      return /* @__PURE__ */ jsxs11(
        "div",
        {
          className: `flex items-center gap-2 border-l pl-3 text-sm transition-colors duration-500 ${isLightNav ? "border-slate-300/30" : "border-sky-100/10"}`,
          children: [
            /* @__PURE__ */ jsx20(
              Link3,
              {
                to: languageSwitch.dePath,
                className: `transition-colors duration-500 ${languageSwitch.current === "de" ? isLightNav ? "font-semibold text-slate-900" : "font-semibold text-white" : isLightNav ? "text-slate-400 hover:text-slate-900" : "text-white/75 hover:text-white"}`,
                children: "DE"
              }
            ),
            /* @__PURE__ */ jsx20("span", { className: `transition-colors duration-500 ${isLightNav ? "text-slate-300" : "text-white/40"}`, children: "|" }),
            /* @__PURE__ */ jsx20(
              Link3,
              {
                to: languageSwitch.enPath,
                className: `transition-colors duration-500 ${languageSwitch.current === "en" ? isLightNav ? "font-semibold text-slate-900" : "font-semibold text-white" : isLightNav ? "text-slate-400 hover:text-slate-900" : "text-white/75 hover:text-white"}`,
                children: "EN"
              }
            )
          ]
        }
      );
    }
    if (isLanguageDropdown(languageSwitch)) {
      return /* @__PURE__ */ jsxs11("div", { ref: langDropdownRef, className: "relative", children: [
        /* @__PURE__ */ jsxs11(
          "button",
          {
            onClick: () => setIsLangDropdownOpen(!isLangDropdownOpen),
            className: `flex items-center gap-1 rounded-lg px-3 py-1.5 text-[13px] font-semibold transition-colors duration-500 ${isLightNav ? "text-slate-500 hover:bg-slate-100 hover:text-slate-900" : "text-white/90 hover:bg-white/10 hover:text-white"}`,
            children: [
              languageSwitch.current.toUpperCase(),
              /* @__PURE__ */ jsx20("svg", { className: "h-3 w-3", viewBox: "0 0 20 20", fill: "currentColor", children: /* @__PURE__ */ jsx20("path", { fillRule: "evenodd", d: "M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z", clipRule: "evenodd" }) })
            ]
          }
        ),
        isLangDropdownOpen && /* @__PURE__ */ jsx20("div", { className: "absolute right-0 top-full z-50 mt-2 min-w-[140px] overflow-hidden rounded-xl border border-[#dce2eb] bg-white py-1 shadow-lg", children: languageSwitch.options.map((opt) => /* @__PURE__ */ jsxs11(
          "button",
          {
            onClick: () => {
              opt.onSelect();
              setIsLangDropdownOpen(false);
            },
            className: `flex w-full items-center gap-2.5 px-4 py-2 text-sm transition-colors ${opt.code === languageSwitch.current ? "bg-sky-50 font-medium text-sky-600" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`,
            children: [
              /* @__PURE__ */ jsx20("span", { className: "font-mono text-xs uppercase", children: opt.code }),
              /* @__PURE__ */ jsx20("span", { children: opt.label })
            ]
          },
          opt.code
        )) })
      ] });
    }
    return /* @__PURE__ */ jsx20(
      "button",
      {
        onClick: languageSwitch.onSwitch,
        className: `text-[13px] font-semibold rounded-lg px-3 py-1.5 transition-colors duration-500 ${isLightNav ? "text-slate-500 hover:text-slate-900 hover:bg-slate-100" : "text-white/90 hover:text-white hover:bg-white/10"}`,
        children: languageSwitch.label
      }
    );
  };
  return /* @__PURE__ */ jsxs11(Fragment2, { children: [
    /* @__PURE__ */ jsx20("nav", { className: "fixed inset-x-0 top-0 z-50 px-3 pb-2 pt-6 sm:px-5 lg:px-8", children: /* @__PURE__ */ jsxs11("div", { className: "mx-auto flex w-full max-w-[1380px] items-center gap-4", children: [
      logo && /* @__PURE__ */ jsx20(
        Link3,
        {
          to: homeLink,
          "aria-label": brandName,
          className: "relative hidden shrink-0 xl:inline-flex",
          style: { height: 40, width: logoWidth },
          children: renderLogo("lg")
        }
      ),
      /* @__PURE__ */ jsx20(
        "div",
        {
          className: `w-full rounded-[999px] px-4 backdrop-blur-2xl transition-all duration-500 sm:px-6 lg:px-7 ${isLightNav ? isScrolled ? "bg-white/65 shadow-[0_8px_32px_rgba(15,23,42,0.08)]" : "bg-white/45 shadow-[0_4px_24px_rgba(15,23,42,0.04)]" : isScrolled ? "bg-[rgba(8,18,38,0.70)] shadow-[0_8px_32px_rgba(1,8,24,0.35)]" : "bg-[rgba(8,18,38,0.55)] shadow-[0_4px_24px_rgba(1,8,24,0.22)]"}`,
          children: /* @__PURE__ */ jsxs11("div", { className: "flex items-center justify-between py-3", children: [
            /* @__PURE__ */ jsx20(
              Link3,
              {
                to: homeLink,
                "aria-label": brandName,
                className: logo ? "relative h-8 w-[150px] sm:h-9 sm:w-[160px] xl:hidden" : void 0,
                children: renderLogo("sm")
              }
            ),
            /* @__PURE__ */ jsxs11("div", { className: "hidden items-center xl:flex xl:w-full", children: [
              /* @__PURE__ */ jsx20("div", { className: "flex items-center gap-5", children: links.map(renderDesktopLink) }),
              /* @__PURE__ */ jsxs11("div", { className: "ml-auto flex items-center gap-5", children: [
                dropdown && /* @__PURE__ */ jsxs11(
                  "div",
                  {
                    ref: dropdownRef,
                    className: "relative flex items-center",
                    onMouseEnter: handleDropdownEnter,
                    onMouseLeave: handleDropdownLeave,
                    children: [
                      /* @__PURE__ */ jsxs11(
                        "button",
                        {
                          type: "button",
                          className: `inline-flex items-center gap-2 text-[14px] font-medium transition-colors duration-500 ${isLightNav ? isDropdownActive || isDropdownOpen ? "text-slate-900" : "text-slate-600 hover:text-slate-900" : isDropdownActive || isDropdownOpen ? "text-white" : "text-white/90 hover:text-white"}`,
                          "aria-expanded": isDropdownOpen,
                          "aria-haspopup": "true",
                          children: [
                            /* @__PURE__ */ jsx20(
                              "span",
                              {
                                "aria-hidden": "true",
                                className: `h-1.5 w-1.5 rounded-full transition-all duration-500 ${isLightNav ? isDropdownActive ? "bg-sky-500 opacity-100" : "bg-slate-300 opacity-0" : isDropdownActive ? "bg-sky-300 opacity-100" : "bg-sky-200/60 opacity-0"}`
                              }
                            ),
                            /* @__PURE__ */ jsx20("span", { children: dropdown.label }),
                            /* @__PURE__ */ jsx20(
                              "svg",
                              {
                                className: `h-3.5 w-3.5 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`,
                                fill: "none",
                                viewBox: "0 0 24 24",
                                stroke: "currentColor",
                                strokeWidth: 2.5,
                                children: /* @__PURE__ */ jsx20("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M19 9l-7 7-7-7" })
                              }
                            )
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsx20(
                        "div",
                        {
                          className: `absolute right-0 top-full z-50 w-[420px] pt-4 transition-all duration-200 ${isDropdownOpen ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none -translate-y-1 opacity-0"}`,
                          children: /* @__PURE__ */ jsx20(
                            "div",
                            {
                              className: `overflow-hidden rounded-2xl backdrop-blur-2xl transition-colors duration-500 ${isLightNav ? "border border-slate-200/60 bg-white shadow-[0_8px_32px_rgba(15,23,42,0.12)]" : "border border-sky-100/10 bg-[rgba(6,14,32,0.92)] shadow-[0_8px_32px_rgba(1,8,24,0.50)]"}`,
                              children: /* @__PURE__ */ jsxs11("div", { className: "p-5", children: [
                                /* @__PURE__ */ jsx20("p", { className: `mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] ${isLightNav ? "text-slate-400" : "text-sky-100/50"}`, children: dropdown.label }),
                                /* @__PURE__ */ jsx20("div", { className: "space-y-1", children: dropdown.items.map((item) => /* @__PURE__ */ jsxs11(
                                  Link3,
                                  {
                                    to: item.to,
                                    className: `group flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors ${isLightNav ? isActivePath(item.to) ? "bg-slate-100" : "hover:bg-slate-50" : isActivePath(item.to) ? "bg-sky-900/30" : "hover:bg-sky-900/20"}`,
                                    children: [
                                      item.icon && /* @__PURE__ */ jsx20("span", { className: `mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isLightNav ? item.iconColorLight ?? "" : item.iconColorDark ?? ""}`, children: /* @__PURE__ */ jsx20("svg", { className: "h-4 w-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, children: /* @__PURE__ */ jsx20("path", { strokeLinecap: "round", strokeLinejoin: "round", d: item.icon }) }) }),
                                      /* @__PURE__ */ jsxs11("div", { children: [
                                        /* @__PURE__ */ jsx20("p", { className: `text-sm font-semibold ${isLightNav ? "text-slate-800 group-hover:text-slate-900" : "text-sky-50 group-hover:text-white"}`, children: item.label }),
                                        item.description && /* @__PURE__ */ jsx20("p", { className: `text-xs ${isLightNav ? "text-slate-500" : "text-sky-100/50"}`, children: item.description })
                                      ] })
                                    ]
                                  },
                                  item.to
                                )) }),
                                blogPosts && blogPosts.length > 0 && /* @__PURE__ */ jsxs11(Fragment2, { children: [
                                  /* @__PURE__ */ jsx20("div", { className: `my-4 border-t ${isLightNav ? "border-slate-200/60" : "border-sky-100/10"}` }),
                                  blogPostsLabel && /* @__PURE__ */ jsx20("p", { className: `mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${isLightNav ? "text-slate-400" : "text-sky-100/50"}`, children: blogPostsLabel }),
                                  /* @__PURE__ */ jsx20("div", { className: "space-y-0.5", children: blogPosts.map((post) => /* @__PURE__ */ jsxs11(
                                    Link3,
                                    {
                                      to: post.linkTo,
                                      className: `group flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${isLightNav ? "text-slate-600 hover:bg-slate-50 hover:text-slate-900" : "text-sky-100/60 hover:bg-sky-900/20 hover:text-white"}`,
                                      children: [
                                        /* @__PURE__ */ jsx20(
                                          "svg",
                                          {
                                            className: `h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5 ${isLightNav ? "text-slate-400" : "text-sky-100/40"}`,
                                            fill: "none",
                                            viewBox: "0 0 24 24",
                                            stroke: "currentColor",
                                            strokeWidth: 2,
                                            children: /* @__PURE__ */ jsx20("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M9 5l7 7-7 7" })
                                          }
                                        ),
                                        /* @__PURE__ */ jsx20("span", { className: "truncate", children: post.title })
                                      ]
                                    },
                                    post.slug
                                  )) })
                                ] })
                              ] })
                            }
                          )
                        }
                      )
                    ]
                  }
                ),
                renderDesktopLanguageSwitch(),
                /* @__PURE__ */ jsx20(
                  Link3,
                  {
                    to: cta.to,
                    onClick: cta.onClick ? (e) => {
                      e.preventDefault();
                      cta.onClick?.();
                    } : void 0,
                    className: `ml-1 inline-flex items-center justify-center rounded-2xl px-5 py-2.5 text-sm font-semibold transition-all duration-500 ${isLightNav ? "bg-slate-900 text-white shadow-[0_4px_16px_rgba(15,23,42,0.18)] hover:bg-slate-800 hover:shadow-[0_8px_24px_rgba(15,23,42,0.22)]" : "bg-white/90 text-slate-900 shadow-[0_4px_16px_rgba(1,8,24,0.25)] hover:-translate-y-[1px] hover:bg-white"}`,
                    children: cta.label
                  }
                )
              ] })
            ] }),
            /* @__PURE__ */ jsx20(
              "button",
              {
                className: "rounded-xl p-2 xl:hidden",
                onClick: () => setIsMenuOpen(true),
                "aria-label": "Open menu",
                children: /* @__PURE__ */ jsx20(
                  "svg",
                  {
                    className: `h-6 w-6 transition-colors duration-500 ${isLightNav ? "text-slate-900" : "text-sky-100"}`,
                    fill: "none",
                    viewBox: "0 0 24 24",
                    stroke: "currentColor",
                    children: /* @__PURE__ */ jsx20("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 6h16M4 12h16M4 18h16" })
                  }
                )
              }
            )
          ] })
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx20(
      "div",
      {
        className: `fixed inset-0 z-40 bg-slate-950/35 backdrop-blur-sm transition-opacity duration-300 xl:hidden ${isMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"}`,
        onClick: closeMobileMenu,
        "aria-hidden": !isMenuOpen
      }
    ),
    /* @__PURE__ */ jsx20(
      "div",
      {
        "data-testid": "mobile-drawer",
        className: `fixed right-0 top-0 z-50 h-full w-80 max-w-[calc(100vw-2rem)] transform border-l border-sky-100/20 bg-[#08152f]/95 shadow-2xl backdrop-blur-xl transition-transform duration-300 ease-out xl:hidden ${isMenuOpen ? "translate-x-0" : "translate-x-full"}`,
        "aria-hidden": !isMenuOpen,
        children: /* @__PURE__ */ jsxs11("div", { className: "flex h-full flex-col", children: [
          /* @__PURE__ */ jsxs11("div", { className: "flex items-center justify-between border-b border-sky-100/20 p-5", children: [
            logo ? /* @__PURE__ */ jsx20("img", { src: logo.dark, alt: logo.alt, className: "h-7 w-auto", loading: "lazy" }) : /* @__PURE__ */ jsx20("span", { className: "text-lg font-bold text-white", children: brandName }),
            /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-3", children: [
              languageSwitch && isLanguageLinks(languageSwitch) && /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-1.5 text-sm", children: [
                /* @__PURE__ */ jsx20(
                  Link3,
                  {
                    to: languageSwitch.dePath,
                    className: languageSwitch.current === "de" ? "font-semibold text-white" : "text-sky-100/60 hover:text-white",
                    onClick: closeMobileMenu,
                    children: "DE"
                  }
                ),
                /* @__PURE__ */ jsx20("span", { className: "text-sky-100/35", children: "|" }),
                /* @__PURE__ */ jsx20(
                  Link3,
                  {
                    to: languageSwitch.enPath,
                    className: languageSwitch.current === "en" ? "font-semibold text-white" : "text-sky-100/60 hover:text-white",
                    onClick: closeMobileMenu,
                    children: "EN"
                  }
                )
              ] }),
              languageSwitch && isLanguageDropdown(languageSwitch) && /* @__PURE__ */ jsx20("div", { className: "flex items-center gap-1.5 text-sm", children: languageSwitch.options.map((opt, i) => /* @__PURE__ */ jsxs11("span", { className: "flex items-center gap-1.5", children: [
                i > 0 && /* @__PURE__ */ jsx20("span", { className: "text-sky-100/25", children: "|" }),
                /* @__PURE__ */ jsx20(
                  "button",
                  {
                    onClick: () => {
                      opt.onSelect();
                      closeMobileMenu();
                    },
                    className: opt.code === languageSwitch.current ? "font-semibold text-white" : "text-sky-100/60 hover:text-white",
                    children: opt.code.toUpperCase()
                  }
                )
              ] }, opt.code)) }),
              /* @__PURE__ */ jsx20("button", { onClick: closeMobileMenu, className: "rounded-lg p-2", "aria-label": "Close", children: /* @__PURE__ */ jsx20("svg", { className: "h-5 w-5 text-sky-100/90", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx20("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M6 18L18 6M6 6l12 12" }) }) })
            ] })
          ] }),
          /* @__PURE__ */ jsx20("div", { className: "flex-1 overflow-y-auto p-5", children: /* @__PURE__ */ jsxs11("div", { className: "space-y-4", children: [
            /* @__PURE__ */ jsx20("div", { className: "space-y-0.5", children: links.map((link) => {
              const isActive = isActivePath(link.to);
              return /* @__PURE__ */ jsxs11(
                Link3,
                {
                  to: link.to,
                  className: `flex items-center gap-2 rounded-xl px-4 py-2.5 transition-colors ${isActive ? "bg-sky-900/35 text-white" : "text-sky-100/90 hover:bg-sky-900/35 hover:text-white"}`,
                  onClick: closeMobileMenu,
                  children: [
                    /* @__PURE__ */ jsx20(
                      "span",
                      {
                        "aria-hidden": "true",
                        className: `h-1.5 w-1.5 rounded-full ${isActive ? "bg-sky-300 opacity-100" : "bg-sky-200/60 opacity-0"}`
                      }
                    ),
                    /* @__PURE__ */ jsx20("span", { children: link.label })
                  ]
                },
                `mobile-${link.to}`
              );
            }) }),
            dropdown && /* @__PURE__ */ jsxs11("div", { children: [
              /* @__PURE__ */ jsxs11(
                "button",
                {
                  type: "button",
                  onClick: () => setIsMobileDropdownOpen(!isMobileDropdownOpen),
                  className: "flex w-full items-center justify-between px-4 pb-1.5",
                  children: [
                    /* @__PURE__ */ jsx20("span", { className: "text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-100/55", children: dropdown.label }),
                    /* @__PURE__ */ jsx20(
                      "svg",
                      {
                        className: `h-3.5 w-3.5 text-sky-100/55 transition-transform duration-200 ${isMobileDropdownOpen ? "rotate-180" : ""}`,
                        fill: "none",
                        viewBox: "0 0 24 24",
                        stroke: "currentColor",
                        strokeWidth: 2.5,
                        children: /* @__PURE__ */ jsx20("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M19 9l-7 7-7-7" })
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsxs11("div", { className: `space-y-0.5 overflow-hidden transition-all duration-200 ${isMobileDropdownOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`, children: [
                dropdown.items.map((item) => /* @__PURE__ */ jsxs11(
                  Link3,
                  {
                    to: item.to,
                    className: `flex items-center gap-2 rounded-xl px-4 py-2.5 transition-colors ${isActivePath(item.to) ? "bg-sky-900/35 text-white" : "text-sky-100/90 hover:bg-sky-900/35 hover:text-white"}`,
                    onClick: closeMobileMenu,
                    children: [
                      /* @__PURE__ */ jsx20(
                        "span",
                        {
                          "aria-hidden": "true",
                          className: `h-1.5 w-1.5 rounded-full ${isActivePath(item.to) ? "bg-sky-300 opacity-100" : "bg-sky-200/60 opacity-0"}`
                        }
                      ),
                      /* @__PURE__ */ jsx20("span", { children: item.label })
                    ]
                  },
                  item.to
                )),
                blogPosts && blogPosts.length > 0 && /* @__PURE__ */ jsxs11("div", { className: "mt-2 border-t border-sky-100/10 pt-2", children: [
                  blogPostsLabel && /* @__PURE__ */ jsx20("p", { className: "px-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-100/40", children: blogPostsLabel }),
                  blogPosts.map((post) => /* @__PURE__ */ jsxs11(
                    Link3,
                    {
                      to: post.linkTo,
                      className: "flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-sky-100/70 transition-colors hover:bg-sky-900/35 hover:text-white",
                      onClick: closeMobileMenu,
                      children: [
                        /* @__PURE__ */ jsx20("svg", { className: "h-3 w-3 shrink-0 text-sky-100/40", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, children: /* @__PURE__ */ jsx20("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M9 5l7 7-7 7" }) }),
                        /* @__PURE__ */ jsx20("span", { className: "truncate", children: post.title })
                      ]
                    },
                    post.slug
                  ))
                ] })
              ] })
            ] })
          ] }) }),
          /* @__PURE__ */ jsxs11("div", { className: "border-t border-sky-100/20 p-6 space-y-3", children: [
            /* @__PURE__ */ jsx20(
              Link3,
              {
                to: cta.to,
                className: "flex w-full items-center justify-center rounded-2xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 transition-all hover:bg-white/90",
                onClick: (e) => {
                  if (cta.onClick) {
                    e.preventDefault();
                    cta.onClick();
                  }
                  closeMobileMenu();
                },
                children: cta.label
              }
            ),
            languageSwitch && !isLanguageLinks(languageSwitch) && !isLanguageDropdown(languageSwitch) && /* @__PURE__ */ jsx20(
              "button",
              {
                onClick: () => {
                  languageSwitch.onSwitch();
                  closeMobileMenu();
                },
                className: "flex w-full items-center justify-center rounded-2xl border border-sky-100/20 px-5 py-2.5 text-sm font-medium text-sky-100/90 transition-colors hover:bg-sky-900/35 hover:text-white",
                children: languageSwitch.label
              }
            )
          ] })
        ] })
      }
    )
  ] });
}

// src/components/Hero.tsx
import { jsx as jsx21, jsxs as jsxs12 } from "react/jsx-runtime";
function Hero({
  label,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  badges,
  trustIndicator,
  illustration,
  className = ""
}) {
  const badgeVariants = {
    blue: "bg-blue-50 text-blue-600 border-blue-200",
    orange: "bg-orange-50 text-orange-600 border-orange-200",
    green: "bg-green-50 text-green-600 border-green-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200"
  };
  return /* @__PURE__ */ jsxs12(
    "section",
    {
      className: `relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-purple-50/20 pt-24 pb-16 ${className}`,
      children: [
        /* @__PURE__ */ jsxs12("div", { className: "absolute inset-0 overflow-hidden pointer-events-none", children: [
          /* @__PURE__ */ jsx21("div", { className: "absolute -top-40 -right-40 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl" }),
          /* @__PURE__ */ jsx21("div", { className: "absolute top-1/2 -left-20 w-80 h-80 bg-blue-200/30 rounded-full blur-3xl" }),
          /* @__PURE__ */ jsx21("div", { className: "absolute bottom-20 right-1/4 w-64 h-64 bg-orange-200/20 rounded-full blur-3xl" })
        ] }),
        /* @__PURE__ */ jsx21("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8 relative z-10", children: /* @__PURE__ */ jsxs12("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center", children: [
          /* @__PURE__ */ jsxs12("div", { className: "max-w-xl", children: [
            label && /* @__PURE__ */ jsx21("p", { className: "inline-flex items-center px-4 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium border border-primary-200 mb-6", children: label }),
            /* @__PURE__ */ jsx21("h1", { className: "text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight", children: title }),
            /* @__PURE__ */ jsx21("p", { className: "text-lg md:text-xl text-gray-600 mb-8 leading-relaxed", children: subtitle }),
            badges && badges.length > 0 && /* @__PURE__ */ jsx21("div", { className: "flex flex-wrap gap-3 mb-10", children: badges.map((badge, index) => /* @__PURE__ */ jsxs12(
              "span",
              {
                className: `inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${badgeVariants[badge.variant || "blue"]}`,
                children: [
                  badge.icon,
                  badge.text
                ]
              },
              index
            )) }),
            /* @__PURE__ */ jsxs12("div", { className: "flex flex-col sm:flex-row gap-4", children: [
              primaryCta && /* @__PURE__ */ jsxs12(
                "a",
                {
                  href: primaryCta.href,
                  className: "inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/25",
                  children: [
                    primaryCta.text,
                    /* @__PURE__ */ jsx21(
                      "svg",
                      {
                        className: "w-4 h-4",
                        viewBox: "0 0 24 24",
                        fill: "none",
                        xmlns: "http://www.w3.org/2000/svg",
                        children: /* @__PURE__ */ jsx21(
                          "path",
                          {
                            d: "M5 12H19M19 12L12 5M19 12L12 19",
                            stroke: "currentColor",
                            strokeWidth: "2",
                            strokeLinecap: "round",
                            strokeLinejoin: "round"
                          }
                        )
                      }
                    )
                  ]
                }
              ),
              secondaryCta && /* @__PURE__ */ jsx21(
                "a",
                {
                  href: secondaryCta.href,
                  className: "inline-flex items-center justify-center px-6 py-3.5 rounded-full border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors",
                  children: secondaryCta.text
                }
              )
            ] }),
            trustIndicator && /* @__PURE__ */ jsx21("div", { className: "mt-12", children: trustIndicator })
          ] }),
          illustration && /* @__PURE__ */ jsx21("div", { className: "relative hidden lg:block", children: illustration })
        ] }) })
      ]
    }
  );
}

// src/components/LegalPage.tsx
import { Link as Link4 } from "react-router-dom";
import { jsx as jsx22, jsxs as jsxs13 } from "react/jsx-runtime";
function LegalPage({ title, backLabel, backTo, children }) {
  return /* @__PURE__ */ jsx22("section", { className: "pt-24 pb-16 md:py-24", children: /* @__PURE__ */ jsx22(Container, { children: /* @__PURE__ */ jsxs13("div", { className: "max-w-3xl mx-auto", children: [
    /* @__PURE__ */ jsxs13(
      Link4,
      {
        to: backTo,
        className: "inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700 mb-8 transition-colors",
        children: [
          /* @__PURE__ */ jsx22(
            "svg",
            {
              className: "w-4 h-4",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx22(
                "path",
                {
                  d: "M19 12H5M5 12L12 19M5 12L12 5",
                  strokeLinecap: "round",
                  strokeLinejoin: "round"
                }
              )
            }
          ),
          backLabel
        ]
      }
    ),
    /* @__PURE__ */ jsx22("h1", { className: "text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-8", children: title }),
    /* @__PURE__ */ jsx22("article", { className: "rounded-[18px] border border-[#dce2eb] bg-[#eef1f6] p-6 md:p-10 space-y-8", children })
  ] }) }) });
}

// src/components/NotFoundPage.tsx
import { Link as Link5 } from "react-router-dom";
import { jsx as jsx23, jsxs as jsxs14 } from "react/jsx-runtime";
function NotFoundPage({ title, description, backLabel, backTo = "/" }) {
  return /* @__PURE__ */ jsx23("div", { className: "min-h-[70vh] flex items-center justify-center px-4 py-16", children: /* @__PURE__ */ jsxs14("div", { className: "text-center max-w-md mx-auto", children: [
    /* @__PURE__ */ jsx23("p", { className: "text-8xl font-bold text-primary-500 mb-4", children: "404" }),
    /* @__PURE__ */ jsx23("h1", { className: "text-2xl md:text-3xl font-bold text-slate-900 mb-4", children: title }),
    /* @__PURE__ */ jsx23("p", { className: "text-slate-500 mb-8", children: description }),
    /* @__PURE__ */ jsxs14(
      Link5,
      {
        to: backTo,
        className: "inline-flex items-center gap-2 rounded-lg bg-slate-900 py-3 px-6 text-sm font-semibold text-white transition-colors hover:bg-slate-800",
        children: [
          /* @__PURE__ */ jsx23(
            "svg",
            {
              className: "w-4 h-4",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx23("path", { d: "M19 12H5M5 12L12 19M5 12L12 5", strokeLinecap: "round", strokeLinejoin: "round" })
            }
          ),
          backLabel
        ]
      }
    )
  ] }) });
}

// src/components/PageHeroSection.tsx
import { jsx as jsx24, jsxs as jsxs15 } from "react/jsx-runtime";
function PageHeroSection({ label, title, subtitle, className = "" }) {
  return /* @__PURE__ */ jsx24("section", { className: `pt-28 pb-16 md:pt-36 md:pb-24 bg-white ${className}`, children: /* @__PURE__ */ jsx24(Container, { children: /* @__PURE__ */ jsx24(AnimatedSection, { children: /* @__PURE__ */ jsxs15("div", { className: "mx-auto max-w-3xl text-center", children: [
    label && /* @__PURE__ */ jsx24("p", { className: "text-sm font-semibold uppercase tracking-widest text-teal-500 text-center mb-4", children: label }),
    /* @__PURE__ */ jsx24("h1", { className: "text-3xl font-bold tracking-tight text-slate-900 md:text-5xl lg:text-6xl", children: title }),
    subtitle && /* @__PURE__ */ jsx24("p", { className: "mt-4 text-lg md:text-xl text-slate-500", children: subtitle })
  ] }) }) }) });
}

// src/components/ContactPage.tsx
import { zodResolver } from "@hookform/resolvers/zod";
import { useState as useState4 } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// src/components/SpamGuard.tsx
import { useCallback as useCallback5, useRef as useRef6 } from "react";

// src/common/spamGuard.ts
var SPAM_GUARD_FIELD = "website";
var SPAM_GUARD_ELAPSED_FIELD = "elapsedMs";

// src/components/SpamGuard.tsx
import { jsx as jsx25, jsxs as jsxs16 } from "react/jsx-runtime";
function useSpamGuard() {
  const mountedAt = useRef6(Date.now());
  const honeypotRef = useRef6(null);
  const getSpamGuardFields = useCallback5(
    () => ({
      [SPAM_GUARD_FIELD]: honeypotRef.current?.value ?? "",
      [SPAM_GUARD_ELAPSED_FIELD]: Date.now() - mountedAt.current
    }),
    []
  );
  return { honeypotRef, getSpamGuardFields };
}
var OFFSCREEN_STYLE = {
  position: "absolute",
  left: "-9999px",
  width: "1px",
  height: "1px",
  overflow: "hidden"
};
function HoneypotField({ inputRef }) {
  return /* @__PURE__ */ jsx25("div", { style: OFFSCREEN_STYLE, "aria-hidden": "true", children: /* @__PURE__ */ jsxs16("label", { children: [
    "Website",
    /* @__PURE__ */ jsx25(
      "input",
      {
        ref: inputRef,
        type: "text",
        name: SPAM_GUARD_FIELD,
        tabIndex: -1,
        autoComplete: "off"
      }
    )
  ] }) });
}

// src/components/ContactPage.tsx
import { jsx as jsx26, jsxs as jsxs17 } from "react/jsx-runtime";
function buildContactSchema(messages) {
  return z.object({
    name: z.string().min(2, messages.nameMin),
    email: z.string().email(messages.emailInvalid),
    company: z.string().optional(),
    message: z.string().min(10, messages.messageMin)
  });
}
var DEFAULT_VALIDATION = {
  nameMin: "Name must be at least 2 characters",
  emailInvalid: "Please enter a valid email address",
  messageMin: "Message must be at least 10 characters"
};
function ContactPage({ labels, endpoint = "/api/contact" }) {
  const contactSchema = buildContactSchema(labels.validation ?? DEFAULT_VALIDATION);
  const [submitStatus, setSubmitStatus] = useState4(
    "idle"
  );
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(contactSchema)
  });
  const { honeypotRef, getSpamGuardFields } = useSpamGuard();
  const onSubmit = async (data) => {
    const guard = getSpamGuardFields();
    if (guard.website) {
      setSubmitStatus("success");
      return;
    }
    setSubmitStatus("submitting");
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, ...guard })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      window.umami?.track("contact-submit");
      setSubmitStatus("success");
    } catch {
      setSubmitStatus("error");
    }
  };
  return /* @__PURE__ */ jsxs17("div", { children: [
    /* @__PURE__ */ jsx26(PageHeroSection, { title: labels.title, subtitle: labels.subtitle }),
    /* @__PURE__ */ jsx26("section", { className: "pb-16 md:pb-24", children: /* @__PURE__ */ jsx26(Container, { children: /* @__PURE__ */ jsxs17("div", { className: "max-w-2xl mx-auto", children: [
      submitStatus === "success" && /* @__PURE__ */ jsx26(
        "div",
        {
          "data-testid": "contact-success",
          className: "mb-6 rounded-lg bg-teal-50 border border-teal-200 p-4 text-teal-700",
          children: labels.successMessage ?? "Message sent successfully."
        }
      ),
      submitStatus === "error" && /* @__PURE__ */ jsx26(
        "div",
        {
          "data-testid": "contact-error",
          className: "mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-red-700",
          children: labels.errorMessage ?? "Something went wrong."
        }
      ),
      /* @__PURE__ */ jsxs17(
        "form",
        {
          "data-testid": "contact-form",
          onSubmit: handleSubmit(onSubmit),
          className: "space-y-6",
          noValidate: true,
          children: [
            /* @__PURE__ */ jsxs17("div", { children: [
              /* @__PURE__ */ jsx26("label", { htmlFor: "contact-name", className: "block text-sm font-medium text-slate-700", children: labels.nameLabel }),
              /* @__PURE__ */ jsx26(
                "input",
                {
                  id: "contact-name",
                  type: "text",
                  ...register("name"),
                  className: "mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-900 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                }
              ),
              errors.name && /* @__PURE__ */ jsx26("p", { className: "mt-1 text-sm text-red-600", children: errors.name.message })
            ] }),
            /* @__PURE__ */ jsxs17("div", { children: [
              /* @__PURE__ */ jsx26("label", { htmlFor: "contact-email", className: "block text-sm font-medium text-slate-700", children: labels.emailLabel }),
              /* @__PURE__ */ jsx26(
                "input",
                {
                  id: "contact-email",
                  type: "email",
                  ...register("email"),
                  className: "mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-900 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                }
              ),
              errors.email && /* @__PURE__ */ jsx26("p", { className: "mt-1 text-sm text-red-600", children: errors.email.message })
            ] }),
            /* @__PURE__ */ jsxs17("div", { children: [
              /* @__PURE__ */ jsx26(
                "label",
                {
                  htmlFor: "contact-company",
                  className: "block text-sm font-medium text-slate-700",
                  children: labels.companyLabel ?? "Company"
                }
              ),
              /* @__PURE__ */ jsx26(
                "input",
                {
                  id: "contact-company",
                  type: "text",
                  ...register("company"),
                  className: "mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-900 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs17("div", { children: [
              /* @__PURE__ */ jsx26(
                "label",
                {
                  htmlFor: "contact-message",
                  className: "block text-sm font-medium text-slate-700",
                  children: labels.messageLabel
                }
              ),
              /* @__PURE__ */ jsx26(
                "textarea",
                {
                  id: "contact-message",
                  rows: 5,
                  ...register("message"),
                  className: "mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-900 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                }
              ),
              errors.message && /* @__PURE__ */ jsx26("p", { className: "mt-1 text-sm text-red-600", children: errors.message.message })
            ] }),
            /* @__PURE__ */ jsx26(HoneypotField, { inputRef: honeypotRef }),
            /* @__PURE__ */ jsx26(
              "button",
              {
                type: "submit",
                disabled: submitStatus === "submitting",
                className: "w-full rounded-lg bg-slate-900 py-3 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed",
                children: labels.submitButton
              }
            )
          ]
        }
      )
    ] }) }) })
  ] });
}

// src/components/ThankYouPage.tsx
import { useEffect as useEffect9 } from "react";
import { Link as Link6 } from "react-router-dom";
import { jsx as jsx27, jsxs as jsxs18 } from "react/jsx-runtime";
function ThankYouPage({
  labels,
  homePath,
  nextStepUrl,
  onMount,
  onCtaClick
}) {
  useEffect9(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    return () => robots.remove();
  }, []);
  useEffect9(() => {
    onMount?.();
  }, []);
  const showNextStep = Boolean(labels.nextStep && nextStepUrl);
  return /* @__PURE__ */ jsx27("section", { className: "bg-[#f2f5fa] pt-20 pb-24 min-h-screen", children: /* @__PURE__ */ jsx27(Container, { children: /* @__PURE__ */ jsx27("div", { className: "max-w-2xl mx-auto", children: /* @__PURE__ */ jsxs18("div", { className: "rounded-[18px] border border-[#dce2eb] bg-white p-8 md:p-12 text-center", children: [
    /* @__PURE__ */ jsx27("div", { className: "w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6", children: /* @__PURE__ */ jsx27(
      "svg",
      {
        className: "w-8 h-8 text-green-600",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "2.5",
        children: /* @__PURE__ */ jsx27("path", { d: "M20 6L9 17L4 12", strokeLinecap: "round", strokeLinejoin: "round" })
      }
    ) }),
    /* @__PURE__ */ jsx27("h1", { className: "text-3xl md:text-4xl font-semibold text-gray-900 mb-4", children: labels.title }),
    /* @__PURE__ */ jsx27("p", { className: "text-gray-600 mb-10", children: labels.subtitle }),
    showNextStep && labels.nextStep && nextStepUrl && /* @__PURE__ */ jsxs18("div", { className: "bg-slate-900 rounded-[18px] p-6 text-white text-left mb-8", children: [
      /* @__PURE__ */ jsx27("h2", { className: "font-semibold text-lg mb-2", children: labels.nextStep.heading }),
      /* @__PURE__ */ jsx27("p", { className: "text-sm text-white/80 mb-4", children: labels.nextStep.description }),
      /* @__PURE__ */ jsxs18(
        "a",
        {
          href: nextStepUrl,
          target: "_blank",
          rel: "noopener noreferrer",
          onClick: onCtaClick,
          className: "inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors w-full justify-center",
          children: [
            labels.nextStep.cta,
            /* @__PURE__ */ jsx27(
              "svg",
              {
                className: "w-4 h-4",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                strokeWidth: "2",
                children: /* @__PURE__ */ jsx27(
                  "path",
                  {
                    d: "M5 12H19M19 12L12 5M19 12L12 19",
                    strokeLinecap: "round",
                    strokeLinejoin: "round"
                  }
                )
              }
            )
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs18(
      Link6,
      {
        to: homePath,
        className: "text-sm text-gray-500 hover:text-gray-900 transition-colors",
        children: [
          "\u2190 ",
          labels.backHome
        ]
      }
    )
  ] }) }) }) });
}

// src/components/WorksWithSection.tsx
import { jsx as jsx28, jsxs as jsxs19 } from "react/jsx-runtime";
function WorksWithSection({
  label,
  heading,
  headingHighlight,
  description,
  agents,
  cards
}) {
  return /* @__PURE__ */ jsxs19(
    "section",
    {
      id: "works-with",
      "data-nav-theme": "light",
      className: "relative z-20 bg-[#f2f5fa] pb-20 pt-20 md:pb-28 md:pt-28",
      children: [
        /* @__PURE__ */ jsx28("div", { className: "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsx28(AnimatedSection, { children: /* @__PURE__ */ jsxs19("div", { className: "mx-auto mb-6 max-w-3xl text-center", children: [
          label && /* @__PURE__ */ jsx28("p", { className: "mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-sky-500", children: label }),
          /* @__PURE__ */ jsxs19("h2", { className: "font-display text-4xl font-bold tracking-tight text-slate-900 md:text-5xl lg:text-6xl", children: [
            heading,
            " ",
            headingHighlight && /* @__PURE__ */ jsx28("span", { className: "bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent", children: headingHighlight })
          ] }),
          description && /* @__PURE__ */ jsx28("p", { className: "mx-auto mt-5 max-w-xl text-lg leading-relaxed text-slate-600", children: description })
        ] }) }) }),
        /* @__PURE__ */ jsx28(AnimatedSection, { delay: 160, children: /* @__PURE__ */ jsx28("div", { className: "px-4 sm:px-6 lg:px-10", children: /* @__PURE__ */ jsx28(
          "div",
          {
            "data-testid": "works-with-agent-grid",
            className: "grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5",
            children: agents.map((agent, i) => /* @__PURE__ */ jsx28(
              "div",
              {
                className: "relative flex h-[76px] items-center justify-center rounded-xl border border-[#dce3ed] bg-[#e8edf4] px-6 text-sm font-semibold text-slate-600 transition-all duration-200 hover:border-[#c8d1de] hover:bg-[#dfe6ef] hover:text-slate-900",
                children: agent.name
              },
              `${agent.name}-${i}`
            ))
          }
        ) }) }),
        cards && cards.length > 0 && /* @__PURE__ */ jsx28("div", { className: "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsx28("div", { className: "mt-20 grid grid-cols-1 gap-6 lg:grid-cols-3", children: cards.map((card, index) => /* @__PURE__ */ jsx28(AnimatedSection, { delay: (index + 2) * 120, children: /* @__PURE__ */ jsxs19("div", { className: "flex h-full flex-col rounded-[18px] border border-[#dce2eb] bg-[#eef1f6] p-8", children: [
          /* @__PURE__ */ jsx28("span", { className: "mb-4 text-3xl", children: card.icon }),
          /* @__PURE__ */ jsx28("h3", { className: "mb-2 text-lg font-semibold text-slate-900", children: card.title }),
          /* @__PURE__ */ jsx28("p", { className: "text-[0.94rem] leading-relaxed text-slate-700", children: card.description })
        ] }) }, card.title)) }) })
      ]
    }
  );
}

// src/components/PageLoadingIndicator.tsx
import { jsx as jsx29 } from "react/jsx-runtime";
function PageLoadingIndicator() {
  return /* @__PURE__ */ jsx29(
    "div",
    {
      role: "status",
      "aria-label": "Loading page",
      className: "flex justify-center items-center min-h-[60vh] py-20",
      children: /* @__PURE__ */ jsx29("div", { className: "animate-spin w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full" })
    }
  );
}

// src/components/Pricing.tsx
import { jsx as jsx30, jsxs as jsxs20 } from "react/jsx-runtime";
function Pricing({
  label,
  title,
  tiers,
  popularBadge = "Most Popular",
  gridCols,
  className = ""
}) {
  const cols = gridCols ?? (tiers.length <= 3 ? 3 : 4);
  const gridClass = cols === 4 ? "grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4 items-stretch" : "grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 items-stretch";
  return /* @__PURE__ */ jsxs20("div", { className, children: [
    /* @__PURE__ */ jsx30("section", { "data-testid": "pricing-hero", className: "pt-28 pb-16 md:pt-36 md:pb-24 bg-white", children: /* @__PURE__ */ jsx30(Container, { children: /* @__PURE__ */ jsxs20(AnimatedSection, { children: [
      label && /* @__PURE__ */ jsx30("p", { className: "text-sm font-semibold uppercase tracking-widest text-teal-500 text-center mb-4", children: label }),
      /* @__PURE__ */ jsx30("h1", { className: "text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 text-center", children: title })
    ] }) }) }),
    /* @__PURE__ */ jsx30("section", { className: "pb-16 md:pb-24", children: /* @__PURE__ */ jsx30(Container, { children: /* @__PURE__ */ jsx30(AnimatedSection, { children: /* @__PURE__ */ jsx30("div", { "data-testid": "pricing-grid", className: gridClass, children: tiers.map((tier) => /* @__PURE__ */ jsxs20(
      "div",
      {
        "data-testid": `pricing-card-${tier.name.toLowerCase()}`,
        className: `relative flex h-full flex-col rounded-2xl border p-6 sm:p-8 ${tier.popular ? "border-sky-500 ring-2 ring-sky-500 shadow-xl bg-slate-900 text-white" : "border-slate-200 bg-white shadow-sm"}`,
        children: [
          tier.popular && /* @__PURE__ */ jsx30(
            "span",
            {
              "data-testid": "pricing-popular-badge",
              className: "absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-sky-500 px-4 py-1 text-sm font-semibold text-white",
              children: popularBadge
            }
          ),
          /* @__PURE__ */ jsx30("h2", { className: `text-xl font-bold ${tier.popular ? "text-white" : "text-slate-900"}`, children: tier.name }),
          /* @__PURE__ */ jsxs20("div", { className: "mt-4 flex items-baseline gap-1", children: [
            /* @__PURE__ */ jsx30("span", { className: `text-4xl md:text-5xl font-bold ${tier.popular ? "text-white" : "text-slate-900"}`, children: tier.price }),
            tier.period && /* @__PURE__ */ jsx30("span", { className: `text-base ${tier.popular ? "text-slate-300" : "text-slate-500"}`, children: tier.period })
          ] }),
          tier.subtitle && /* @__PURE__ */ jsx30("p", { className: `mt-2 text-sm ${tier.popular ? "text-slate-300" : "text-slate-600"}`, children: tier.subtitle }),
          /* @__PURE__ */ jsx30("ul", { className: "mt-6 mb-8 flex-grow space-y-3", children: tier.features.map((feature) => /* @__PURE__ */ jsxs20("li", { className: "flex items-start gap-2", children: [
            /* @__PURE__ */ jsx30(
              "svg",
              {
                className: `h-5 w-5 flex-shrink-0 ${tier.popular ? "text-sky-300" : "text-teal-500"}`,
                fill: "none",
                viewBox: "0 0 24 24",
                strokeWidth: 2,
                stroke: "currentColor",
                children: /* @__PURE__ */ jsx30("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M4.5 12.75l6 6 9-13.5" })
              }
            ),
            /* @__PURE__ */ jsx30("span", { className: `text-sm ${tier.popular ? "text-slate-200" : "text-slate-600"}`, children: feature })
          ] }, feature)) }),
          /* @__PURE__ */ jsx30(
            "a",
            {
              href: tier.ctaHref ?? "#",
              className: `block w-full rounded-lg py-3 px-4 text-center text-sm font-semibold transition-colors ${tier.popular ? "bg-sky-500 text-white hover:bg-sky-600" : "bg-slate-900 text-white hover:bg-slate-800"}`,
              children: tier.cta
            }
          )
        ]
      },
      tier.name
    )) }) }) }) })
  ] });
}

// src/components/Section.tsx
import { jsx as jsx31, jsxs as jsxs21 } from "react/jsx-runtime";
function Section({
  children,
  id,
  className = "",
  background = "white",
  padding = "lg"
}) {
  const backgrounds = {
    white: "bg-white",
    muted: "bg-muted-50",
    gradient: "bg-gradient-to-br from-white via-blue-50/30 to-purple-50/20"
  };
  const paddings = {
    sm: "py-12 md:py-16",
    md: "py-16 md:py-20",
    lg: "py-20 md:py-28"
  };
  return /* @__PURE__ */ jsx31("section", { id, className: `${backgrounds[background]} ${paddings[padding]} ${className}`, children: /* @__PURE__ */ jsx31("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8", children }) });
}
function SectionHeader({
  label,
  title,
  subtitle,
  align = "center",
  className = ""
}) {
  const alignClasses = {
    left: "text-left",
    center: "text-center max-w-3xl mx-auto"
  };
  return /* @__PURE__ */ jsxs21("div", { className: `mb-16 ${alignClasses[align]} ${className}`, children: [
    label && /* @__PURE__ */ jsx31("p", { className: "inline-flex items-center px-4 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium border border-primary-200 mb-4", children: label }),
    /* @__PURE__ */ jsx31("h2", { className: "text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4", children: title }),
    subtitle && /* @__PURE__ */ jsx31("p", { className: "text-lg text-gray-600 max-w-2xl mx-auto", children: subtitle })
  ] });
}

// src/components/sections/CTASection.tsx
import { Fragment as Fragment3, jsx as jsx32, jsxs as jsxs22 } from "react/jsx-runtime";
function CTASection({ headline, headlineHighlight, button, href, card }) {
  return /* @__PURE__ */ jsx32("section", { className: "pb-12 pt-24 md:pb-16 md:pt-32", children: /* @__PURE__ */ jsx32("div", { className: "px-6 sm:px-10 lg:px-16", children: /* @__PURE__ */ jsxs22("div", { className: "flex flex-col items-start justify-between gap-12 lg:flex-row lg:items-end lg:gap-16", children: [
    /* @__PURE__ */ jsxs22("div", { className: "flex-shrink-0", children: [
      /* @__PURE__ */ jsxs22("h2", { className: "font-display text-4xl font-bold tracking-tight text-slate-900 md:text-5xl lg:text-6xl", children: [
        headline.split("\n").map((line, i) => /* @__PURE__ */ jsxs22("span", { children: [
          i > 0 && /* @__PURE__ */ jsx32("br", {}),
          line
        ] }, i)),
        headlineHighlight && /* @__PURE__ */ jsxs22(Fragment3, { children: [
          " ",
          /* @__PURE__ */ jsx32("br", {}),
          /* @__PURE__ */ jsx32(
            "span",
            {
              "data-highlight": true,
              className: "bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent",
              children: headlineHighlight
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsx32("div", { className: "mt-8", children: /* @__PURE__ */ jsx32(
        "a",
        {
          href,
          className: "inline-flex items-center gap-2 rounded-full bg-slate-900 px-8 py-4 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:bg-slate-800 hover:shadow-xl",
          children: button
        }
      ) })
    ] }),
    card && /* @__PURE__ */ jsxs22("div", { className: "w-full max-w-md rounded-2xl bg-[#e8edf4] p-8 lg:p-10", children: [
      /* @__PURE__ */ jsx32("h3", { className: "text-xl font-bold tracking-tight text-slate-900", children: card.title }),
      /* @__PURE__ */ jsx32("p", { className: "mt-3 text-sm leading-relaxed text-slate-600", children: card.description }),
      /* @__PURE__ */ jsx32(
        "a",
        {
          href: card.href,
          className: "mt-5 inline-block text-sm font-medium text-slate-900 underline decoration-slate-900/40 underline-offset-4 transition-colors hover:decoration-slate-900",
          children: card.link
        }
      )
    ] })
  ] }) }) });
}

// src/components/sections/Testimonials.tsx
import { useRef as useRef7, useState as useState5 } from "react";
import { jsx as jsx33, jsxs as jsxs23 } from "react/jsx-runtime";
var INITIAL_COUNT = 4;
function Testimonials({
  title,
  subtitle,
  items,
  showMoreLabel,
  showLessLabel,
  prevLabel = "Previous",
  nextLabel = "Next"
}) {
  const scrollRef = useRef7(null);
  const [showAll, setShowAll] = useState5(false);
  const visibleItems = showAll ? items : items.slice(0, INITIAL_COUNT);
  const canExpand = showMoreLabel && showLessLabel && items.length > INITIAL_COUNT;
  function scrollCarousel(direction) {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: direction === "next" ? scrollAmount : -scrollAmount,
      behavior: "smooth"
    });
  }
  return /* @__PURE__ */ jsx33("section", { id: "testimonials", className: "py-20 md:py-28 bg-dark-900", children: /* @__PURE__ */ jsxs23("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8", children: [
    (title || subtitle) && /* @__PURE__ */ jsxs23("div", { className: "text-center max-w-3xl mx-auto mb-16", children: [
      title && /* @__PURE__ */ jsx33("h2", { className: "text-4xl md:text-5xl font-bold text-white mb-6", children: title }),
      subtitle && /* @__PURE__ */ jsx33("p", { className: "text-lg text-gray-400", children: subtitle })
    ] }),
    /* @__PURE__ */ jsx33(
      "div",
      {
        ref: scrollRef,
        className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 overflow-x-auto snap-x snap-mandatory md:overflow-x-visible md:snap-none",
        children: visibleItems.map((testimonial, index) => /* @__PURE__ */ jsx33("div", { className: "snap-start min-w-[85vw] md:min-w-0", children: /* @__PURE__ */ jsx33(TestimonialCard, { testimonial }) }, index))
      }
    ),
    /* @__PURE__ */ jsxs23("div", { className: "flex justify-center gap-4 mt-6 md:hidden", children: [
      /* @__PURE__ */ jsx33(
        "button",
        {
          "aria-label": prevLabel,
          onClick: () => scrollCarousel("prev"),
          className: "w-10 h-10 rounded-full bg-dark-800 border border-dark-700 flex items-center justify-center text-white hover:bg-dark-700 transition-colors",
          children: /* @__PURE__ */ jsx33(
            "svg",
            {
              className: "w-5 h-5",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx33("path", { d: "M15 19L8 12L15 5", strokeLinecap: "round", strokeLinejoin: "round" })
            }
          )
        }
      ),
      /* @__PURE__ */ jsx33(
        "button",
        {
          "aria-label": nextLabel,
          onClick: () => scrollCarousel("next"),
          className: "w-10 h-10 rounded-full bg-dark-800 border border-dark-700 flex items-center justify-center text-white hover:bg-dark-700 transition-colors",
          children: /* @__PURE__ */ jsx33(
            "svg",
            {
              className: "w-5 h-5",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx33("path", { d: "M9 5L16 12L9 19", strokeLinecap: "round", strokeLinejoin: "round" })
            }
          )
        }
      )
    ] }),
    canExpand && /* @__PURE__ */ jsx33("div", { className: "text-center mt-12", children: /* @__PURE__ */ jsxs23(
      "button",
      {
        onClick: () => setShowAll(!showAll),
        className: "btn-secondary bg-transparent border-gray-600 text-white hover:bg-dark-800 inline-flex items-center gap-2",
        children: [
          showAll ? showLessLabel : showMoreLabel,
          /* @__PURE__ */ jsx33(
            "svg",
            {
              className: `w-4 h-4 transition-transform duration-300 ${showAll ? "rotate-180" : ""}`,
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx33("path", { d: "M6 9L12 15L18 9", strokeLinecap: "round", strokeLinejoin: "round" })
            }
          )
        ]
      }
    ) })
  ] }) });
}
function TestimonialCard({ testimonial }) {
  return /* @__PURE__ */ jsxs23("div", { className: "bg-dark-800 rounded-3xl p-8 border border-dark-700 hover:border-dark-600 transition-all duration-300 group h-full flex flex-col", children: [
    /* @__PURE__ */ jsxs23("p", { className: "text-gray-300 mb-8 leading-relaxed flex-grow", children: [
      "\u201C",
      testimonial.quote,
      "\u201D"
    ] }),
    /* @__PURE__ */ jsxs23("div", { className: "flex items-center gap-4 mt-auto", children: [
      testimonial.avatar && /* @__PURE__ */ jsx33("div", { className: "w-12 h-12 rounded-xl overflow-hidden flex-shrink-0", children: /* @__PURE__ */ jsx33(
        "img",
        {
          src: testimonial.avatar,
          alt: testimonial.name,
          className: "w-full h-full object-cover"
        }
      ) }),
      /* @__PURE__ */ jsxs23("div", { children: [
        /* @__PURE__ */ jsx33("p", { className: "font-semibold text-white", children: testimonial.name }),
        /* @__PURE__ */ jsxs23("p", { className: "text-sm text-gray-500", children: [
          testimonial.role,
          testimonial.company ? `, ${testimonial.company}` : ""
        ] })
      ] })
    ] })
  ] });
}

// src/hooks/useDocumentTitle.ts
import { useContext as useContext3, useEffect as useEffect10 } from "react";
function documentTitleTags(config, options) {
  const {
    title,
    appendSiteName = true,
    description,
    breadcrumbs
  } = typeof options === "string" ? { title: options } : options ?? {};
  const fullTitle = !title ? config.siteName : appendSiteName ? `${title} | ${config.siteName}` : title;
  return {
    title: fullTitle,
    meta: [
      ...description ? [{ name: "description", content: description }] : [],
      { property: "og:title", content: fullTitle },
      ...description ? [{ property: "og:description", content: description }] : []
    ],
    jsonLd: breadcrumbs && breadcrumbs.length > 0 ? [{ key: "breadcrumb", data: breadcrumbSchema(config.baseUrl, breadcrumbs) }] : []
  };
}
function useDocumentTitle(options) {
  const config = useContext3(BrandConfigContext);
  const collector = useContext3(HeadCollectorContext);
  if (config) collector?.add(documentTitleTags(config, options));
  useEffect10(() => {
    if (!config) return;
    const tags = documentTitleTags(config, options);
    document.title = tags.title;
    const undo = applyHeadTags(tags);
    return () => {
      document.title = config.siteName;
      undo();
    };
  }, [config, options]);
}
export {
  AnimatedSection,
  BentoGrid,
  Button,
  CTA,
  CTASection,
  ContactPage,
  Container,
  ContentSection,
  DarkCard,
  DarkMeshSection,
  FAQ,
  FeatureCard,
  Features,
  Footer,
  HeadCollectorContext,
  Hero,
  HoneypotField,
  IntegrationFunnel,
  LandingProvider,
  Layout,
  LegalPage,
  MeshBackground,
  Navbar,
  NotFoundPage,
  PageHeroSection,
  PageLoadingIndicator,
  PageTransition,
  Pricing,
  ScrollToTop,
  Section,
  SectionHeader,
  SeoHead,
  SmoothScrollLink,
  StructuredData,
  Testimonials,
  ThankYouPage,
  WorksWithSection,
  availableFlows,
  breadcrumbSchema,
  buildSummary,
  createHeadCollector,
  emptyAnswers,
  firstOpenStep,
  flowOptions,
  isComplete,
  isStepAnswered,
  nonStandardPicks,
  organizationSchema,
  systemsForRoles,
  useBrandConfig,
  useDocumentTitle,
  useScrollAnimation,
  useSpamGuard,
  websiteSchema
};
//# sourceMappingURL=index.mjs.map